// ============================================
// INTERACTIVE TIMELINE - ERA & EVENT DATA
// ============================================
// Comprehensive data structure for the Interactive Morse & Radio History Timeline
// 8 Eras spanning 1844-2024, with detailed events, artifacts, and unlock requirements

/**
 * ERA UNLOCK TYPES:
 * - 'always': Always unlocked (Era 1)
 * - 'soft': Soft lock - show warning but allow access
 * - 'hard': Hard lock - must meet requirements
 */

/**
 * UNLOCK REQUIREMENT STRUCTURE:
 * {
 *   type: 'any' | 'all',
 *   count: number (for 'any' type),
 *   requirements: [
 *     { id: 'wpm_XX', label: 'Reach XX WPM', check: (stats) => stats.bestWPM >= XX },
 *     { id: 'skill_X', label: 'Unlock Skill Level X', check: (stats) => stats.skillLevel >= X },
 *     // ... more requirements
 *   ]
 * }
 */

// ============================================
// ERA DEFINITIONS
// ============================================

export const TIMELINE_ERAS = [
    // ============================================
    // ERA 1: 1838-1860 - THE TELEGRAPH AGE
    // ============================================
    {
        id: 1,
        number: 1,
        title: "The Telegraph Age",
        period: "1838-1860",
        subtitle: "The Birth of Telecommunications",
        description: "Experience the revolutionary moment when Samuel Morse sent the first telegraph message, changing human communication forever.",
        theme: "vintage_telegraph",
        unlockType: "always", // Always unlocked
        unlockRequirements: null,
        recommendedSkills: [],
        backgroundColor: "#2c1810",
        accentColor: "#d4a574",
        icon: "⚡",
        iconImage: "/assets/timeline/icons/era1.png",
        totalEvents: 7,

        // Detailed event data for Era 1
        events: [
            {
                id: "era1_event_speedwell",
                eraNumber: 1,
                eventNumber: 0.5,
                title: "The Birth of the Code",
                date: "January 6, 1838",
                location: "Speedwell Ironworks, NJ",
                message: "A PATIENT WAITER IS NO LOSER",
                difficulty: 1,
                historicalContext: "Years before the famous Washington demonstration, Alfred Vail and Samuel Morse successfully tested their system at the Speedwell Ironworks. Vail had recently refined the code, assigning shorter sequences to common letters (like 'E'). This trusted demonstration over 2 miles of wire proved the system's viability.",
                technology: {
                    name: "Vail's Lever Key",
                    description: "Alfred Vail's mechanical improvements turned Morse's concept into a practical device.",
                },
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Speedwell Test", description: "Decode the first public message", speed: 10 }
                    ],
                    hints: ["Patience is key", "Not a loser", "6 words"],
                    passingAccuracy: 70,
                },
                rewards: { xp: 125 }
            },
            {
                id: "era1_first_telegram",
                eraNumber: 1,
                eventNumber: 1,
                title: "The First Telegram",
                date: "May 24, 1844",
                location: "Washington D.C. to Baltimore",
                message: "WHAT HATH GOD WROUGHT",
                difficulty: 1, // 1-5 stars
                historicalContext: `On May 24, 1844, Samuel F.B. Morse sent the first official telegram from the U.S. Capitol in Washington D.C. to his assistant Alfred Vail in Baltimore, Maryland. The message was chosen by Annie Ellsworth, daughter of a friend, from the Bible (Numbers 23:23).

This 35-mile telegraph line was funded by Congress with $30,000 and proved the viability of electric telegraphy. The message was sent using Morse's original code and recorded on paper tape using indentations, not the audible clicks we associate with Morse code today.`,

                technology: {
                    name: "Morse Register (Paper Tape)",
                    description: "The original telegraph didn't make sounds - it marked dots and dashes on paper tape using an electromagnetic stylus.",
                    visualMode: true, // Supports visual decoding challenge
                },

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: CW Audio",
                            description: "Decode the message using modern morse code tones",
                            audioType: "standard", // Use modern beep tones, not sounder
                            speed: 10, // WPM
                            hasAmbience: true,
                            ambienceFile: null, // Horse carriages, 1840s street sounds
                        },
                        {
                            id: "visual",
                            name: "Visual: Paper Tape Animation",
                            description: "Decode from animated paper tape indentations (as Morse saw it)",
                            visualAnimation: true,
                            noAudio: true, // Visual only - no sound
                            speed: 8, // Slower for visual
                        },
                        {
                            id: "expert",
                            name: "Expert: Sounder Audio and Tape",
                            description: "Authentic 1844 sounder clicks with paper tape visual at increased speed",
                            audioType: "sounder_1844", // Use historical sounder audio
                            audioFile: null, // TO BE SOURCED - historical sounder audio
                            visualAnimation: true, // Show paper tape with audio
                            speed: 15,
                            perfectCopyRequired: true,
                        }
                    ],
                    hints: [
                        "This message is from the Bible, Numbers 23:23",
                        "The phrase asks about God's power",
                        "Focus on the rhythm - dits are short, dahs are long"
                    ],
                    passingAccuracy: 70, // Minimum 70% to unlock
                },

                artifacts: [
                    {
                        id: "artifact_morse_key",
                        name: "Original Morse Key",
                        description: "A brass telegraph key based on Morse's original design from 1844",
                        rarity: "common",
                        imageFile: "/assets/timeline/era1/morse_key.png",
                        collectionMethod: "event_completion",
                    },
                    {
                        id: "artifact_paper_tape",
                        name: "First Telegram Paper Tape",
                        description: "Historic reproduction of the paper tape showing 'What hath God wrought'",
                        rarity: "common",
                        imageFile: "/assets/timeline/era1/paper_tape.png",
                        collectionMethod: "event_completion",
                    },
                    {
                        id: "artifact_code_chart_1844",
                        name: "Morse Code Chart (1844)",
                        description: "The original Morse code character set before international standardization",
                        rarity: "rare",
                        imageFile: "/assets/timeline/era1/code_chart.png",
                        collectionMethod: "perfect_copy",
                    }
                ],

                triviaLinks: [107], // 1858 message time

                quiz: [
                    {
                        question: "What year was the first telegram sent?",
                        options: ["1842", "1844", "1846", "1848"],
                        correctAnswer: 1, // Index of correct answer
                        explanation: "The first telegram was sent on May 24, 1844.",
                    },
                    {
                        question: "Who funded Morse's experimental telegraph line?",
                        options: ["Private investors", "The British Crown", "U.S. Congress", "Samuel Morse himself"],
                        correctAnswer: 2,
                        explanation: "Congress appropriated $30,000 to build the experimental line from Washington to Baltimore.",
                    },
                    {
                        question: "How long was the first telegraph line?",
                        options: ["15 miles", "25 miles", "35 miles", "50 miles"],
                        correctAnswer: 2,
                        explanation: "The line ran approximately 35 miles from Washington D.C. to Baltimore, Maryland.",
                    }
                ],

                rewards: {
                    xp: 100,
                    achievements: ["telegraph_pioneer"],
                    themes: ["vintage_telegraph"],
                    nextEraHint: "Continue to Era 2: Transatlantic Cable (1858)",
                }
            },
            {
                id: "era1_event_key",
                eraNumber: 1,
                eventNumber: 2,
                title: "The Straight Key",
                date: "1845",
                location: "Vail's Workshop",
                message: "KEY CLOSED CIRCUIT OPEN",
                difficulty: 1,
                historicalContext: "Alfred Vail improved Morse's original apparatus with a reliable mechanical key. The 'Straight Key' allowed operators to control the duration of dots and dashes precisely by hand. It became the standard tool for the next century.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Manual Keying", description: "Decode the manual sending", speed: 12 }
                    ],
                    hints: ["Standard key type", "Circuit state", "Action"],
                    passingAccuracy: 80,
                },
                artifacts: [
                    { id: "artifact_camelback_key", name: "Camelback Key", description: "Early lever key with a distinctive humped shape", rarity: "common", collectionMethod: "event_completion", imageFile: "/assets/timeline/era1/camelback_key.png" }
                ],
                rewards: { xp: 150 },
                triviaLinks: [108], // Straight key history
            },

            // Placeholder events for Era 1 (to be fully developed)
            {
                id: "era1_event2",
                eraNumber: 1,
                eventNumber: 2,
                title: "Railroad Telegraph Expansion",
                date: "September 22, 1851",
                location: "Erie Railroad, New York",
                message: "HOLD TRAIN AT GOSHEN FOR EXPRESS",
                difficulty: 1,
                historicalContext: "Before the telegraph, trains ran on strict timetables and 'right of way' rules. If a train was delayed, others had to wait for hours, unsure if the track was clear.\n\nIn 1851, Charles Minot, Superintendent of the Erie Railroad, sent the first telegraphic train order. He used the wire to confirm a train's location and ordered another to proceed, revolutionizing railroad safety and efficiency forever. This event marked the beginning of 'dispatching' by telegraph.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Dispatcher",
                            description: "Decode the urgent train order",
                            speed: 12,
                        }
                    ],
                    hints: [
                        "Message is a command to stop a train",
                        "Contains the town name 'GOSHEN'",
                        "Standard railroad brevity"
                    ],
                    passingAccuracy: 70,
                },

                artifacts: [
                    {
                        id: "artifact_conductor_watch",
                        name: "Conductor's Pocket Watch",
                        description: "Standard issue timepiece essential for railroad scheduling",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era1/conductor_watch.png"
                    }
                ],
                triviaLinks: [77], // Train horn Q

                quiz: [
                    {
                        question: "Why was the telegraph important for railroads?",
                        options: ["It was cheaper than signals", "It allowed real-time dispatching", "Passengers could send messages", "It powered the trains"],
                        correctAnswer: 1,
                        explanation: "Telegraphs allowed dispatchers to know exactly where trains were and safely route them around delays.",
                    }
                ],

                rewards: { xp: 150 }
            },
            {
                id: "era1_event3",
                eraNumber: 1,
                eventNumber: 3,
                title: "Western Union Founded",
                date: "April 4, 1856",
                location: "Rochester, New York",
                message: "SYSTEM CONSOLIDATED NY AND MISSISSIPPI",
                difficulty: 2,
                historicalContext: "In the early 1850s, the telegraph industry was chaotic, with dozens of small, incompatible companies. Hiram Sibley and Ezra Cornell merged their companies to form the 'Western Union Telegraph Company'.\n\nTheir goal was to create a unified, national system. Western Union would go on to build the first transcontinental telegraph line and dominate American communication for over a century.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Corporate Wire",
                            description: "Decode the merger announcement",
                            speed: 13,
                        },
                        {
                            id: "visual",
                            name: "Visual: Stock Ticker",
                            description: "Read the message from a visual ticker tape",
                            visualAnimation: true,
                            noAudio: true,
                            speed: 10,
                        }
                    ],
                    hints: [
                        "Start with 'SYSTEM'",
                        "Mentions two locations: NY and MISSISSIPPI",
                        "Refers to business consolidation"
                    ],
                    passingAccuracy: 75,
                },

                artifacts: [
                    {
                        id: "artifact_stock_ticker",
                        name: "Early Stock Ticker",
                        description: "A machine that printed stock prices transmitted over telegraph lines",
                        rarity: "rare",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era1/stock_ticker.png"
                    }
                ],

                quiz: [
                    {
                        question: "Who founded Western Union?",
                        options: ["Samuel Morse", "Thomas Edison", "Hiram Sibley & Ezra Cornell", "Alexander Bell"],
                        correctAnswer: 2,
                        explanation: "Hiram Sibley and Ezra Cornell merged their rival telegraph companies to form Western Union.",
                    }
                ],

                rewards: { xp: 200 },
                triviaLinks: [109], // Western Union history
            },
            {
                id: "era1_event4",
                eraNumber: 1,
                eventNumber: 4,
                title: "Press Reporting Revolution",
                date: "August 1858",
                location: "New York City",
                message: "QUEEN VICTORIA MESSAGE RECEIVED",
                difficulty: 2,
                historicalContext: "The Associated Press (AP) was formed in 1846 specifically to share the costs of telegraphing news from the Mexican-American War. By 1858, the first (briefly working) Transatlantic Cable allowed news to travel from Europe to America in minutes rather than weeks.\n\nWhen Queen Victoria's message to President Buchanan arrived, it was a sensation, proving that news could be instantaneous globally.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: News Wire",
                            description: "Decode the breaking news headline",
                            speed: 14,
                        }
                    ],
                    hints: [
                        "It's about the British Monarch",
                        "The Queen's name is VICTORIA",
                        "Confirms receipt of a message"
                    ],
                    passingAccuracy: 75,
                },

                artifacts: [
                    {
                        id: "artifact_newspaper_1858",
                        name: "1858 Newspaper Extra",
                        description: "Front page announcing the first transatlantic cable message",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era1/newspaper_1858.png"
                    }
                ],

                quiz: [
                    {
                        question: "What news organization was formed to use the telegraph?",
                        options: ["CNN", "The Associated Press (AP)", "Reuters", "The New York Times"],
                        correctAnswer: 1,
                        explanation: "The Associated Press was founded as a cooperative to share the expensive costs of telegraph news transmission.",
                    }
                ],

                rewards: { xp: 200 },
                triviaLinks: [110], // AP history
            },
            {
                id: "era1_event5",
                eraNumber: 1,
                eventnumber: 6,
                title: "Pacific Telegraph Act",
                date: "June 16, 1860",
                location: "Washington, D.C.",
                message: "CONGRESS APPROVES LINE TO PACIFIC",
                difficulty: 2,
                historicalContext: "By 1860, the telegraph covered the East, but California was still weeks away by mail. Congress passed the Pacific Telegraph Act, authorizing $40,000 a year for ten years to any company that could build a line from Missouri to San Francisco.\n\nThis act set off a race against time and nature to connect the continent, marking the end of the pioneer era and the beginning of the great consolidation.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Legislation",
                            description: "Decode the government mandate",
                            speed: 13,
                        }
                    ],
                    hints: [
                        "Government body involved",
                        "Action taken (APPROVES)",
                        "Destination of the line"
                    ],
                    passingAccuracy: 75,
                },

                artifacts: [
                    {
                        id: "artifact_act_of_congress",
                        name: "Pacific Telegraph Act",
                        description: "Copy of the legislation authorizing the first transcontinental link",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era1/act_of_congress.png"
                    }
                ],

                quiz: [
                    {
                        question: "What was the goal of the Pacific Telegraph Act?",
                        options: ["To ban telegraphs", "To fund a line to California", "To tax telegrams", "To build a railroad"],
                        correctAnswer: 1,
                        explanation: "It provided federal funding and a deadline to connect the East Coast networks to the isolated West Coast.",
                    }
                ],

                rewards: {
                    xp: 250,
                    nextEraHint: "Era 2: Ocean Cables & Era 3: War"
                },
                triviaLinks: [111], // Pacific Telegraph Act
            }
        ]
    },

    // ============================================
    // ERA 2: 1858-1870 - OCEAN CABLES (SKELETON)
    // ============================================
    {
        id: 2,
        number: 2,
        title: "Ocean Cables",
        period: "1858-1870",
        subtitle: "Connecting Continents",
        description: "The ambitious project to lay a telegraph cable across the Atlantic Ocean, linking Europe and America.",
        theme: "victorian_era",
        unlockType: "soft",
        unlockRequirements: {
            type: "any",
            count: 1,
            requirements: [
                {
                    id: "sessions_5",
                    label: "Complete 5 practice sessions",
                    check: (stats) => stats.totalSessions >= 5,
                    progress: (stats) => ({ current: stats.totalSessions, target: 5, unit: "sessions" })
                },
                {
                    id: "chars_100",
                    label: "Copy 100 characters",
                    check: (stats) => stats.totalCharactersCopied >= 100,
                    progress: (stats) => ({ current: stats.totalCharactersCopied, target: 100, unit: "chars" })
                },
                {
                    id: "wpm_10",
                    label: "Reach 10 WPM",
                    check: (stats) => stats.bestWPM >= 10,
                    progress: (stats) => ({ current: stats.bestWPM, target: 10, unit: "WPM" })
                },
            ]
        },
        recommendedSkills: ["Complete 5 practice sessions", "Copy 100 characters", "Reach 10 WPM"],
        backgroundColor: "#1a2332",
        accentColor: "#6b9bd1",
        icon: "🌊",
        iconImage: "/assets/timeline/icons/era2.png",
        totalEvents: 5,
        events: [
            {
                id: "era2_event1",
                eraNumber: 2,
                eventNumber: 1,
                title: "The First Attempt Fails",
                date: "August 11, 1857",
                location: "Atlantic Ocean",
                message: "CABLE SNAPPED DEPTH TWO MILES",
                difficulty: 1,
                historicalContext: "The idea of a cable across the Atlantic seemed impossible. The ocean was 2 miles deep. In 1857, two warships (USS Niagara and HMS Agamemnon) tried to lay the cable meeting in mid-ocean.\n\nDisaster struck when the cable's brake mechanism applied too much tension, snapping the line and losing it to the abyss. It was a humiliating 300,000 pound loss.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Ship Log",
                            description: "Decode the Captain's distress report",
                            speed: 10,
                        }
                    ],
                    hints: [
                        "Describes a mechanical failure",
                        "Mentions depth of water",
                        "The 'CABLE' is the subject"
                    ],
                    passingAccuracy: 70,
                },

                artifacts: [
                    {
                        id: "artifact_gutta_percha",
                        name: "Gutta Percha Sample",
                        description: "Rubbery sap from Malaysian trees used to insulate underwater cables",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era2/gutta_percha.png"
                    }
                ],

                quiz: [
                    {
                        question: "What material insulated early underwater cables?",
                        options: ["Rubber", "Plastic", "Gutta Percha", "Tar"],
                        correctAnswer: 2,
                        explanation: "Gutta Percha was a thermoplastic latex from trees that didn't degrade in saltwater, making ocean cables possible.",
                    }
                ],

                rewards: { xp: 150 },
                triviaLinks: [112], // Gutta Percha
            },
            {
                id: "era2_event2",
                eraNumber: 2,
                eventNumber: 2,
                title: "The Leviathan Launches",
                date: "January 31, 1858",
                location: "London, UK",
                message: "GREAT EASTERN READY FOR VOYAGE",
                difficulty: 2,
                historicalContext: "Isambard Kingdom Brunel built the 'Great Eastern', a ship so huge it was five times larger than any other vessel afloat. It was the only ship capable of carrying the thousands of miles of heavy cable needed to cross the ocean in one go.\n\nKnown as 'The Leviathan', it was destined to be the hero of the transatlantic cable.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Shipyard",
                            description: "Decode the launch announcement",
                            speed: 12,
                        },
                        {
                            id: "visual",
                            name: "Visual: Signal Lamp",
                            description: "Decode visual light signals from the ship",
                            visualAnimation: true,
                            visualType: "lamp",
                            noAudio: true,
                            speed: 8,
                        }
                    ],
                    hints: [
                        "Ship's name fits 'GREAT ...'",
                        "Refers to being prepared",
                        "Mention of a journey/voyage"
                    ],
                    passingAccuracy: 75,
                },

                artifacts: [
                    {
                        id: "artifact_great_eastern_print",
                        name: "Great Eastern Lithograph",
                        description: "A print showing the massive paddle-steamer laying cable",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era2/great_eastern_lithograph.png"
                    }
                ],

                quiz: [
                    {
                        question: "Why was the Great Eastern unique?",
                        options: ["It was the first steamship", "It was the largest ship ever built at the time", "It had no sails", "It was a submarine"],
                        correctAnswer: 1,
                        explanation: "The Great Eastern was an engineering marvel, large enough to carry the entire transatlantic cable in its hold.",
                    }
                ],

                rewards: { xp: 200 },
                triviaLinks: [113], // Great Eastern
            },
            {
                id: "era2_event3",
                eraNumber: 2,
                eventNumber: 3,
                title: "The Lost Cable",
                date: "August 2, 1865",
                location: "Mid-Atlantic",
                message: "GRAPPLING HOOK FAILED TO RECOVER",
                difficulty: 3,
                historicalContext: "In 1865, the Great Eastern was just 600 miles from Newfoundland when the cable snapped again. The crew spent days dragging grappling hooks along the ocean floor 2.5 miles down to find it.\n\nThey successfully hooked it multiple times, but the lifting gear broke each time, dropping the cable back into the darkness. They had to abandon the mission and return to England.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Deep Sea",
                            description: "Decode the failure report",
                            speed: 14,
                        },
                        {
                            id: "visual",
                            name: "Visual: Tension Gauge",
                            description: "Read the strain report visually",
                            visualAnimation: true,
                            visualType: "galvanometer",
                            noAudio: true,
                            speed: 10,
                        }
                    ],
                    hints: [
                        "Tool used to catch the cable",
                        "Outcome was negative",
                        "Action was to get it back"
                    ],
                    passingAccuracy: 80,
                },

                artifacts: [
                    {
                        id: "artifact_grappling_hook",
                        name: "Iron Grappling Hook",
                        description: "Sketch of the device used to fish the cable from the ocean floor",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era2/grappling_hook.png"
                    }
                ],

                quiz: [
                    {
                        question: "How deep was the water where they lost the cable?",
                        options: ["100 feet", "1 mile", "2.5 miles", "10 miles"],
                        correctAnswer: 2,
                        explanation: "The Atlantic floor averages over 2 miles deep, making recovery an almost impossible engineering feat.",
                    }
                ],

                rewards: { xp: 250 },
                triviaLinks: [114], // Cable recovery
            },
            {
                id: "era2_event4",
                eraNumber: 2,
                eventNumber: 4,
                title: "Success at Last",
                date: "July 27, 1866",
                location: "Heart's Content, Newfoundland",
                message: "HEARTS CONTENT NEWFOUNDLAND CONNECTED",
                difficulty: 2,
                historicalContext: "Finally! On July 27, 1866, the Great Eastern pulled the heavy cable ashore at the tiny fishing village of Heart's Content, Newfoundland. \n\nThe connection was perfect. Even more amazingly, the ship went back out, found the lost 1865 cable, spliced it, and completed that one too. The world now had two working cables linking the old world and the new.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Victory",
                            description: "Decode the arrival message",
                            speed: 15,
                        },
                        {
                            id: "visual",
                            name: "Visual: Mirror Galvanometer",
                            description: "Simulating the sensitive light-mirror receiver used for weak ocean signals",
                            visualAnimation: true,
                            visualType: "galvanometer",
                            noAudio: true,
                            speed: 6, // Very slow as ocean signals were weak
                        }
                    ],
                    hints: [
                        "Name of the village where it landed",
                        "Name of the Canadian province",
                        "State of the connection"
                    ],
                    passingAccuracy: 80,
                },

                artifacts: [
                    {
                        id: "artifact_cable_segment",
                        name: "1866 Cable Segment",
                        description: "Authentic souvenir slice of the first successful Atlantic cable",
                        rarity: "legendary",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era2/cable_segment.png"
                    },
                    {
                        id: "artifact_mirror_galvanometer",
                        name: "Mirror Galvanometer",
                        description: "Kelvin's invention that used a mirror and light to read tiny currents",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era2/mirror_galvanometer.png"
                    }
                ],

                quiz: [
                    {
                        question: "What instrument was needed to read the weak ocean signals?",
                        options: ["Sounder", "Mirror Galvanometer", "Telephone", "Speaker"],
                        correctAnswer: 1,
                        explanation: "Lord Kelvin invented the mirror galvanometer, which reflected a beam of light to show tiny electrical pulses that were too weak to click a sounder.",
                    }
                ],

                rewards: {
                    xp: 500,
                    achievements: ["transatlantic"]
                },
                triviaLinks: [115], // Heart's Content
            },
            {
                id: "era2_event5",
                eraNumber: 2,
                eventnumber: 6,
                title: "Global Empire",
                date: "June 23, 1870",
                location: "London to Bombay",
                message: "LONDON TO BOMBAY DIRECT LINE OPEN",
                difficulty: 4,
                historicalContext: "With the Atlantic conquered, the British Empire rushed to connect its colonies. The line to India was critical for controlling the empire.\n\nIn 1870, a direct submarine cable was completed from the UK to India. A message that used to take 6 weeks by ship now took 9 minutes. The world had truly effectively shrunk.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Imperial Wire",
                            description: "Decode the colonial connection message",
                            speed: 18,
                        },
                        {
                            id: "expert",
                            name: "Expert: High Speed",
                            description: "Fast commercial traffic",
                            visualAnimation: true,
                            speed: 20,
                        }
                    ],
                    hints: [
                        "From Capital city",
                        "To Indian city",
                        "Type of connection"
                    ],
                    passingAccuracy: 85,
                },

                artifacts: [
                    {
                        id: "artifact_telegraph_map",
                        name: "1870 Cable Map",
                        description: "Map showing the red lines of telegraphs spanning the British Empire",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era2/telegraph_map.png"
                    }
                ],

                quiz: [
                    {
                        question: "How long did a message to India take before the cable?",
                        options: ["1 day", "1 week", "6 weeks", "1 year"],
                        correctAnswer: 2,
                        explanation: "Before the telegraph, a letter had to travel by ship around Africa or through the Suez, taking about 6 weeks.",
                    }
                ],

                rewards: {
                    xp: 300,
                    themes: ["victorian_red"] // Unlocks Map theme?
                },
                triviaLinks: [116], // British Empire cables
            },
        ],
    },

    // ============================================
    // ERA 3: 1861-1900 - WAR & EXPANSION (SKELETON)
    // ============================================
    {
        id: 3,
        number: 3,
        title: "War & Expansion",
        period: "1861-1900",
        subtitle: "Civil War, Golden Spike, and Western Telegraph",
        description: "Telegraph becomes crucial for military communication and connects the American continent coast-to-coast.",
        theme: "civil_war",
        unlockType: "soft",
        unlockRequirements: {
            type: "any",
            count: 1,
            requirements: [
                {
                    id: "callsigns_50",
                    label: "Copy 50 callsigns",
                    check: (stats) => stats.totalCallsignsCopied >= 50,
                    progress: (stats) => ({ current: stats.totalCallsignsCopied, target: 50, unit: "callsigns" })
                },
                {
                    id: "wpm_15",
                    label: "Reach 15 WPM",
                    check: (stats) => stats.bestWPM >= 15,
                    progress: (stats) => ({ current: stats.bestWPM, target: 15, unit: "WPM" })
                },
                {
                    id: "skill_2",
                    label: "Reach Skill Level 2",
                    check: (stats) => stats.skillLevel >= 2,
                    progress: (stats) => ({ current: stats.skillLevel, target: 2, unit: "level" })
                },
            ]
        },
        recommendedSkills: ["Copy 50 callsigns", "Reach 15 WPM", "Reach Skill Level 2"],
        backgroundColor: "#2b2416",
        accentColor: "#b8860b",
        icon: "⚔️",
        iconImage: "/assets/timeline/icons/era3.png",
        totalEvents: 7,
        events: [
            {
                id: "era3_event1",
                eraNumber: 3,
                eventNumber: 1,
                title: "Civil War Begins",
                date: "April 12, 1861",
                location: "Charleston, South Carolina",
                message: "FORT SUMTER SURRENDERED AFTER 34 HOURS",
                difficulty: 3,
                historicalContext: "The American Civil War was the first 'telegraph war'. President Lincoln spent hours daily in the telegraph office near the White House, communicating directly with his generals.\n\nThe fall of Fort Sumter was one of the first major news events flashed across the nation instantly, rallying troops on both sides. This marked the end of the pioneer era and the beginning of telegraphy as a strategic military tool.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: War Despatch",
                            description: "Decode the urgent military telegram",
                            speed: 15,
                        },
                        {
                            id: "expert",
                            name: "Expert: Military Cipher",
                            description: "Fast transmission with visual tape",
                            visualAnimation: true,
                            speed: 18,
                        }
                    ],
                    hints: [
                        "Starts with the location name",
                        "Refers to giving up (SURRENDERED)",
                        "Mentions a duration in hours"
                    ],
                    passingAccuracy: 80,
                },

                artifacts: [
                    {
                        id: "artifact_civil_war_sounder",
                        name: "Portable Field Sounder",
                        description: "A compact telegraph set used by military signal corps in the field",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era3/civil_war_sounder.png"
                    },
                    {
                        id: "artifact_lincoln_portrait",
                        name: "Lincoln in Telegraph Office",
                        description: "Illustration of the President waiting for news from the front",
                        rarity: "epic",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era3/lincoln_portrait.png"
                    }
                ],
                triviaLinks: [82, 85, 110], // Lincoln and USMTC

                quiz: [
                    {
                        question: "How did Lincoln use the telegraph?",
                        options: ["Only for personal news", "He ignored it", "To command generals directly", "It didn't exist yet"],
                        correctAnswer: 2,
                        explanation: "Lincoln was the first president to effectively use electronic communication to direct military operations in real-time.",
                    }
                ],

                rewards: {
                    xp: 300,
                    themes: ["civil_war"]
                }
            },
            {
                id: "era3_event2",
                eraNumber: 3,
                eventNumber: 2,
                title: "Transcontinental Telegraph",
                date: "October 24, 1861",
                location: "Salt Lake City, Utah",
                message: "PONY EXPRESS RELIEVED OF DUTY",
                difficulty: 2,
                historicalContext: "The Pony Express was romantic, but slow and expensive($5 per letter). When the Western Union transcontinental telegraph line was completed in Salt Lake City, connecting California to the East Coast, the Pony Express became obsolete instantly.\n\nThey ceased operations just two days later. Messages that took 10 days by horse now took seconds.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Western Wire",
                            description: "Decode the end of an era",
                            speed: 15,
                        }
                    ],
                    hints: [
                        "Famous horse mail service",
                        "Phrase meaning fired or finished",
                        "Duty or service"
                    ],
                    passingAccuracy: 75,
                },

                artifacts: [
                    {
                        id: "artifact_pony_express_saddlebag",
                        name: "Pony Express Saddlebag",
                        description: "Leather mochila used to carry mail before the wires took over",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era3/pony_express_saddlebag.png"
                    }
                ],
                triviaLinks: [76, 113], // Pony Express obsolete

                quiz: [
                    {
                        question: "How long did the Pony Express last?",
                        options: ["18 months", "10 years", "50 years", "2 months"],
                        correctAnswer: 0,
                        explanation: "The Pony Express only operated for about 18 months (April 1860 - Oct 1861) before the telegraph replaced it.",
                    }
                ],

                rewards: { xp: 250 }
            },
            {
                id: "era3_event3",
                eraNumber: 3,
                eventNumber: 3,
                title: "The Beardslee Telegraph",
                date: "Summer 1862",
                location: "Virginia Campaign",
                message: "SIGNAL CORPS ADVANCING ON RICHMOND",
                difficulty: 3,
                historicalContext: "During the Civil War, the Union Army established the Signal Corps. They used the portable 'Beardslee' telegraph machine, which used a dial instead of a key and was powered by a hand-cranked magneto.\n\nIt was slower than a key but could be used by soldiers who weren't expert operators. It was crucial for coordinating advances.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Field Report",
                            description: "Decode the troop movement",
                            speed: 12,
                        },
                        {
                            id: "visual",
                            name: "Visual: Beardslee Dial",
                            description: "Read the pointer on the dial (Simulated)",
                            visualAnimation: true,
                            visualType: "dial",
                            noAudio: true,
                            speed: 5, // Dial was slow
                        }
                    ],
                    hints: [
                        "Name of the Army unit (SC)",
                        "Moving forward",
                        "Capital of the Confederacy"
                    ],
                    passingAccuracy: 80,
                },

                artifacts: [
                    {
                        id: "artifact_beardslee_magneto",
                        name: "Beardslee Magneto",
                        description: "Hand-cranked generator for the portable field telegraph",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era3/beardslee_magneto.png"
                    }
                ],

                quiz: [
                    {
                        question: "Why was the Beardslee telegraph used in the field?",
                        options: ["It was faster", "It didn't need batteries", "It was wireless", "It printed text"],
                        correctAnswer: 1,
                        explanation: "The Beardslee used a magneto generator, so it didn't require heavy, fragile acid batteries like standard telegraphs.",
                    }
                ],

                rewards: {
                    xp: 350,
                    achievements: ["battlefield_operator"]
                },
                triviaLinks: [117], // Beardslee telegraph
            },
            {
                id: "era3_event4",
                eraNumber: 3,
                eventNumber: 4,
                title: "The Golden Spike",
                date: "May 10, 1869",
                location: "Promontory Summit, Utah",
                message: "DOT DOT DOT DONE",
                difficulty: 2,
                historicalContext: "The completion of the Transcontinental Railroad was the first mass-media event. Telegraph wires were physically wired to the Golden Spike and the silver maul.\n\nWhen Leland Stanford drove the spike, the blows were heard as clicks in telegraph offices across the country. The operator then tapped out a simple message.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: National Broadcast",
                            description: "Decode the famous one-word announcement",
                            speed: 10, // Slow and dramatic
                        },
                        {
                            id: "expert",
                            name: "Expert: The Blows",
                            description: "Decode the rhythm of the hammer strikes",
                            visualAnimation: true,
                            speed: 6,
                        }
                    ],
                    hints: [
                        "Represents the rhythm of hitting",
                        "The final word signifying completion",
                        "Very short message"
                    ],
                    passingAccuracy: 100, // Requires perfection
                },

                artifacts: [
                    {
                        id: "artifact_golden_spike",
                        name: "The Golden Spike",
                        description: "Replica of the final spike that joined the rails",
                        rarity: "legendary",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era3/golden_spike.png"
                    }
                ],
                triviaLinks: [84, 80], // DONE message, time zones

                quiz: [
                    {
                        question: "How did the nation know the railroad was finished?",
                        options: ["Newspaper next day", "Radio broadcast", "Telegraph connected to the hammer", "Smoke signals"],
                        correctAnswer: 2,
                        explanation: "The telegraph wires were connected to the spike/hammer, essentially 'broadcasting' the physical impact live to the nation.",
                    }
                ],

                rewards: {
                    xp: 500,
                    themes: ["railroad_rust"],
                    achievements: ["coast_to_coast"]
                }
            },
            {
                id: "era3_event_stock",
                eraNumber: 3,
                eventNumber: 5,
                title: "The Stock Ticker",
                date: "1867",
                location: "Wall Street, NY",
                message: "GOLD 162 1/2 W.U. 85 N.Y.C. 109",
                difficulty: 4,
                historicalContext: "Edward Calahan invented the stock ticker in 1867, revolutionizing finance. Before this, 'runner' boys had to run prices from the floor to brokers' offices. Thomas Edison improved it in 1869.\n\nThe constant 'tick-tick-tick' sound of the printing telegraph became the heartbeat of Wall Street, allowing for the first real-time, nationwide financial market.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Price Data", description: "Decode stock symbols and prices", speed: 18 },
                        { id: "visual", name: "Visual: Ticker Tape", description: "Read the paper tape (Visual Only)", visualAnimation: true, visualType: "tape", speed: 15 }
                    ],
                    hints: ["Stock symbols (GOLD, W.U.)", "Numbers represent prices", "Includes fractions (1/2)"],
                    passingAccuracy: 90,
                },
                artifacts: [
                    { id: "artifact_edison_ticker", name: "Edison Universal Ticker", description: "The glass-domed machine that printed stock quotes", rarity: "rare", collectionMethod: "event_completion", imageFile: "/assets/timeline/era3/edison_ticker.png" }
                ],
                rewards: { xp: 400 },
                triviaLinks: [118], // Stock ticker
            },
            {
                id: "era3_event5",
                eraNumber: 3,
                eventnumber: 6,
                title: "Weather Service Created",
                date: "1870",
                location: "Washington, D.C.",
                message: "STORM WARNING HURRICANE MOVING NORTH",
                difficulty: 3,
                historicalContext: "Before the telegraph, you couldn't warn someone downwind of a storm because the storm moved faster than a horse.\n\nWith the telegraph, weather observations could be gathered instantly from hundreds of stations to create synoptic maps. In 1870, Congress created the National Weather Service within the Army Signal Corps for this purpose.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Weather Report",
                            description: "Decode the urgent storm warning",
                            speed: 18,
                        }
                    ],
                    hints: [
                        "Type of alert",
                        "Type of severe storm",
                        "Direction of travel"
                    ],
                    passingAccuracy: 85,
                },

                artifacts: [
                    {
                        id: "artifact_anemometer",
                        name: "1870 Anemometer",
                        description: "Early wind speed gauge used by telegraph weather stations",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era3/anemometer.png"
                    }
                ],

                quiz: [
                    {
                        question: "Why was the telegraph essential for weather forecasting?",
                        options: ["It could detect humidity", "It moved data faster than the wind", "Lightning charged the wires", "Birds sat on the wires"],
                        correctAnswer: 1,
                        explanation: "To predict weather, you need to know what is happening upwind. Only the telegraph could transmit that data faster than the weather itself moved.",
                    }
                ],

                rewards: { xp: 300 },
                triviaLinks: [119], // Weather service
            },
            {
                id: "era3_event6",
                eraNumber: 3,
                eventnumber: 7,
                title: "Train Robbery Foiled",
                date: "June 2, 1899",
                location: "Wilcox, Wyoming",
                message: "STOP WILCOX TRAIN BANDITS ABOARD",
                difficulty: 4,
                historicalContext: "The 'Wild West' ended largely due to the telegraph. Outlaws like Butch Cassidy's Wild Bunch could rob a train, but they couldn't outrun the telegraph message sent to the next town's sheriff.\n\nIn the Wilcox Train Robbery, telegraph operators coordinated the pursuit, turning the vast open range into a shrinking net.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Sheriff's Wire",
                            description: "Decode the warning to the next station",
                            speed: 20,
                        },
                        {
                            id: "expert",
                            name: "Expert: High Speed Chase",
                            description: "Very fast train dispatch",
                            visualAnimation: true,
                            speed: 22,
                        }
                    ],
                    hints: [
                        "Command to halt",
                        "Name of the train/location",
                        "Reason for stopping (criminals)"
                    ],
                    passingAccuracy: 85,
                },

                artifacts: [
                    {
                        id: "artifact_wanted_poster",
                        name: "Wanted Poster",
                        description: "Pinkerton Agency poster for the Wild Bunch distributed via telegraph description",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era3/wanted_poster.png"
                    }
                ],
                triviaLinks: [114, 155], // Wyatt Earp, Wire services

                quiz: [
                    {
                        question: "How did the telegraph help catch outlaws?",
                        options: ["Tased them", "Called 911", "Alerted towns ahead of their escape path", "Took photos"],
                        correctAnswer: 2,
                        explanation: "Lawmen could telegraph descriptions and warrants to towns along the outlaw's escape route, cutting them off.",
                    }
                ],

                rewards: { xp: 400 }
            },
        ],
    },

    // ============================================
    // ERA 4: 1895-1920 - WIRELESS REVOLUTION 
    // ============================================
    {
        id: 4,
        number: 4,
        title: "Wireless Revolution",
        period: "1895-1920",
        subtitle: "Marconi, Titanic, and the Birth of Radio",
        description: "Wireless telegraphy emerges, culminating in the Titanic disaster that changed maritime safety forever.",
        theme: "marconi_blue",
        unlockType: "hard",
        unlockRequirements: {
            type: "any",
            count: 2,
            requirements: [
                {
                    id: "wpm_20_callsigns_50",
                    label: "Reach 20 WPM + Copy 50 callsigns",
                    check: (stats) => stats.bestWPM >= 20 && stats.totalCallsignsCopied >= 50,
                    progress: (stats) => ({ current: stats.totalCallsignsCopied, target: 50, unit: "callsigns" })
                },
                {
                    id: "accurate_sessions_10",
                    label: "10 accurate sessions (80%+)",
                    check: (stats) => stats.accurateSessions >= 10,
                    progress: (stats) => ({ current: stats.accurateSessions, target: 10, unit: "sessions" })
                },
                {
                    id: "skill_3",
                    label: "Reach Skill Level 3",
                    check: (stats) => stats.skillLevel >= 3,
                    progress: (stats) => ({ current: stats.skillLevel, target: 3, unit: "level" })
                },
                {
                    id: "paddle_100",
                    label: "Send 100 chars via paddle",
                    check: (stats) => stats.paddleCharsSent >= 100,
                    progress: (stats) => ({ current: stats.paddleCharsSent, target: 100, unit: "chars" })
                },
            ]
        },
        recommendedSkills: ["Reach 20 WPM", "Copy 50 callsigns", "Complete 10 accurate sessions", "Master paddle sending"],
        backgroundColor: "#0d1b2a",
        accentColor: "#4a9eff",
        icon: "📻",
        iconImage: "/assets/timeline/icons/era4.png",
        totalEvents: 8,
        events: [
            {
                id: "era4_event1",
                eraNumber: 4,
                eventNumber: 1,
                title: "Marconi's First Signal",
                date: "December 12, 1901",
                location: "St. John's, Newfoundland",
                message: "S S S",
                difficulty: 2,
                historicalContext: "Guglielmo Marconi proved the impossible: radio waves could curve around the earth. Waiting on a frozen hill in Newfoundland with a kite antenna, he heard the faint clicking of this short message from Poldhu, Cornwall, across the Atlantic Ocean.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: The Discovery",
                            description: "Decode the first transatlantic signal",
                            speed: 8,
                        },
                        {
                            id: "visual",
                            name: "Visual: Spark Gap",
                            description: "Watch the spark jump",
                            visualAnimation: true,
                            visualType: "spark",
                            speed: 6,
                        }
                    ],
                    hints: [
                        "It's just one letter repeated",
                        "Three dots",
                        "The test signal was 'S'"
                    ],
                    passingAccuracy: 100,
                },

                artifacts: [
                    {
                        id: "artifact_coherer",
                        name: "Marconi Coherer",
                        description: "A glass tube of metal filings used to detect early radio waves",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era4/coherer.png"
                    }
                ],

                quiz: [
                    {
                        question: "How did Marconi receive the first signal?",
                        options: ["A giant tower", "A kite antenna", "A satellite dish", "Underground cable"],
                        correctAnswer: 1,
                        explanation: "Marconi used a kite to lift the antenna wire high enough to catch the faint signal from across the ocean.",
                    }
                ],
                rewards: { xp: 400 },
                triviaLinks: [120], // Marconi's first signal
            },
            {
                id: "era4_event_bug",
                eraNumber: 4,
                eventNumber: 2,
                title: "The Vibroplex Bug",
                date: "1905",
                location: "New York",
                message: "SPEED IS KING WITH THE BUG",
                difficulty: 3,
                historicalContext: "Horace Martin invented the 'Vibroplex' semi-automatic key. By using a pendulum to automatically create strings of dots, it relieved 'glass arm' (RSI) and allowed operators to send much faster.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: High Speed", description: "Decode the bug's fast dits", speed: 25 }
                    ],
                    hints: ["Advantage of the bug", "Royal status", "Device name"],
                    passingAccuracy: 85,
                },
                artifacts: [
                    { id: "artifact_vibroplex", name: "Vibroplex Original", description: "The classic 'bug' key with black japanned base", rarity: "epic", collectionMethod: "event_completion", imageFile: "/assets/timeline/era4/vibroplex_bug.png" }
                ],
                rewards: { xp: 450 },
                triviaLinks: [121], // Vibroplex Bug
            },
            {
                id: "era4_event2",
                eraNumber: 4,
                eventNumber: 2,
                title: "The CQD Call (Republic)",
                date: "January 23, 1909",
                location: "Off Nantucket",
                message: "CQD CQD THIS IS REPUBLIC",
                difficulty: 3,
                historicalContext: "Before SOS became standard, 'CQD' (Come Quick Danger) was the distress call. When the RMS Republic collided with another ship in fog, Jack Binns stayed at his wireless key for 36 hours, guiding rescuers. It was the first time radio saved lives at sea on a massive scale.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Distress",
                            description: "Decode the urgent call for help",
                            speed: 15,
                        },
                        {
                            id: "visual",
                            name: "Visual: Spark Transmitter",
                            description: "See the high-voltage transmission",
                            visualAnimation: true,
                            visualType: "spark",
                            speed: 12,
                        }
                    ],
                    hints: [
                        "The old distress code (CQD)",
                        "Name of the ship",
                        "Includes 'THIS IS'"
                    ],
                    passingAccuracy: 85,
                },

                artifacts: [
                    {
                        id: "artifact_spark_key",
                        name: "Marconi Wireless Set",
                        description: "The complete 1912 apparatus, including magnetic detector, multiple tuner, and spark gap",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era4/spark_key.png"
                    }
                ],

                quiz: [
                    {
                        question: "Who was the hero of the Republic sinking?",
                        options: ["The Captain", "Jack Binns (Wireless Op)", "Marconi", "The cook"],
                        correctAnswer: 1,
                        explanation: "Jack Binns became a worldwide celebrity for staying at his post in the freezing, damaged wireless room to guide rescuers.",
                    }
                ],
                rewards: { xp: 450 },
                triviaLinks: [122], // CQD call
            },
            {
                id: "era4_event_film",
                eraNumber: 4,
                eventNumber: 2.5,
                title: "Dots and Dashes (Film)",
                date: "September 30, 1910",
                location: "New Rochelle, NY",
                message: "SAFE COMBINATION 5 9 2",
                difficulty: 2,
                historicalContext: "The 1910 silent film 'Dots and Dashes' by the Thanhouser Company featured a telegraph operator heroically saving her trapped lover by communicating through a safe wall using Morse code taps. It reflected the public's fascination with the 'romance of the wire' and featured female operators who were common in the industry.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: The Rescue",
                            description: "Decode the combination to open the safe",
                            speed: 12,
                        }
                    ],
                    hints: [
                        "It's a combination",
                        "Three numbers",
                        "Middle number is highest"
                    ],
                    passingAccuracy: 85,
                },
                artifacts: [
                    {
                        id: "artifact_film_poster",
                        name: "Dots and Dashes Still",
                        description: "A scene from the 1910 melodrama showing the operator at work",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era4/dots_and_dashes.jpg"
                    }
                ],
                rewards: { xp: 300 },
                triviaLinks: [172, 174], // Movie trivia
            },
            {
                id: "era4_event3",
                eraNumber: 4,
                eventNumber: 3,
                title: "Titanic Disaster",
                date: "April 15, 1912",
                location: "North Atlantic",
                message: "CQD CQD SOS SOS MGY WE ARE SINKING FAST",
                difficulty: 4,
                historicalContext: "The most famous wireless event in history. Operators Jack Phillips and Harold Bride frantically sent both the old 'CQD' and the new 'SOS' codes. 'MGY' was the Titanic's callsign. Their signals summoned the Carpathia, saving 705 survivors.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: The Final Signals",
                            description: "Decode the desperate plea",
                            speed: 18,
                        },
                        {
                            id: "visual",
                            name: "Visual: Fading Spark",
                            description: "Read the spark as power fails",
                            visualAnimation: true,
                            visualType: "spark",
                            speed: 16,
                        }
                    ],
                    hints: [
                        "Uses both distress codes",
                        "Titanic's callsign is MGY",
                        "Describes the ship's condition"
                    ],
                    passingAccuracy: 90,
                },

                artifacts: [
                    {
                        id: "artifact_titanic_log",
                        name: "Wireless Logbook",
                        description: "Replica of the logbook describing the final messages sent and received",
                        rarity: "legendary",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era4/titanic_log.png"
                    }
                ],

                quiz: [
                    {
                        question: "What callsign did Titanic use?",
                        options: ["TITANIC", "SOS", "MGY", "KPH"],
                        correctAnswer: 2,
                        explanation: "MGY was the Marconi wireless station callsign assigned to the Titanic.",
                    }
                ],
                rewards: {
                    xp: 1000,
                    themes: ["marconi_blue"],
                    achievements: ["unsinkable_spirit"]
                },
                triviaLinks: [123], // Titanic
            },
            {
                id: "era4_event4",
                eraNumber: 4,
                eventNumber: 4,
                title: "WWI Trench Wireless",
                date: "1915",
                location: "Western Front",
                message: "GAS GAS GAS ATTACK SECTOR 4",
                difficulty: 3,
                historicalContext: "WWI saw the first use of portable wireless sets in trenches. When telephone lines were cut by artillery, 'spark' sets were the only link. Codes had to be short and urgent.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Frontline Warning",
                            description: "Decode the chemical attack warning",
                            speed: 20,
                        },
                        {
                            id: "visual",
                            name: "Visual: Field Spark",
                            description: "Portable spark set usage",
                            visualAnimation: true,
                            visualType: "spark",
                            speed: 18,
                        }
                    ],
                    hints: [
                        "Repetition of the threat",
                        "Chemical weapon type",
                        "Location (Sector)"
                    ],
                    passingAccuracy: 80,
                },

                artifacts: [
                    {
                        id: "artifact_trench_set",
                        name: "Mark III Trench Wireless",
                        description: "Portable spark transmitter used by the Royal Engineers",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era4/trench_set.png"
                    }
                ],
                rewards: { xp: 500 },
                triviaLinks: [124], // WWI wireless
            },
            {
                id: "era4_event_sports",
                eraNumber: 4,
                eventNumber: 5,
                title: "The Sports Wire",
                date: "1921",
                location: "Polo Grounds, New York",
                message: "RUTH HITS HOMER YANKEES LEAD",
                difficulty: 3,
                historicalContext: "Before radio broadcasting took over, Western Union telegraph operators sat in press boxes at baseball stadiums and boxing rings, tapping out play-by-play action to pool halls and newspaper offices across the country. This 'playograph' system was the first real-time sports broadcasting network.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Play-by-Play", description: "Decode the live game update", speed: 22 },
                        { id: "visual", name: "Visual: Scoreboard", description: "Read the mechanical scoreboard updates", visualAnimation: true, visualType: "scoreboard", speed: 20 }
                    ],
                    hints: ["Famous baseball player", " Scoring event", "Winning team"],
                    passingAccuracy: 85,
                },
                artifacts: [
                    { id: "artifact_ticker_tape_sport", name: "Baseball Ticker Tape", description: "Paper tape with 1921 World Series scores", rarity: "rare", collectionMethod: "event_completion", imageFile: "/assets/timeline/era4/ticker_tape_sport.png" }
                ],
                rewards: { xp: 450 },
                triviaLinks: [125], // Sports wire
            },
            {
                id: "era4_event5",
                eraNumber: 4,
                eventnumber: 6,
                title: "The Zimmerman Telegram",
                date: "January 1917",
                location: "Room 40, London",
                message: "PROPOSE ALLIANCE MEXICO RECONQUER LOST TERRITORY",
                difficulty: 5,
                historicalContext: "British intelligence (Room 40) intercepted a coded telegram from German Foreign Minister Zimmerman to Mexico, proposing an alliance against the US. The decryption of this message helped draw the United States into WWI.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Intercept",
                            description: "Decode the secret diplomatic cable",
                            speed: 22,
                        },
                        {
                            id: "visual",
                            name: "Visual: Ticker Tape",
                            description: "Room 40 decoding tape",
                            visualAnimation: true,
                            visualType: "tape",
                            speed: 20,
                        }
                    ],
                    hints: [
                        "A proposal between countries",
                        "Mentions 'ALLIANCE'",
                        "Target country is MEXICO"
                    ],
                    passingAccuracy: 90,
                },

                artifacts: [
                    {
                        id: "artifact_codebook",
                        name: "Diplomatic Codebook",
                        description: "German diplomatic code 13040 used for the telegram",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era4/codebook.png"
                    }
                ],
                rewards: {
                    xp: 800,
                    achievements: ["codebreaker"],
                    nextEraHint: "Era 5: The Golden Age"
                },
                triviaLinks: [126], // Zimmerman Telegram
            }
        ],
    },

    // ============================================
    // ERA 5: 1921-1938 - THE GOLDEN AGE
    // ============================================
    {
        id: 5,
        number: 5,
        title: "The Golden Age",
        period: "1919-1938",
        subtitle: "Aviation, Exploration, and Amateur Radio",
        description: "Between the wars, Morse code guided pioneers across oceans and to the poles. Amateur radio operators proved the world was smaller than anyone thought.",
        theme: "art_deco",
        unlockType: "hard",
        unlockRequirements: {
            type: "any",
            count: 1,
            requirements: [
                {
                    id: "wpm_25",
                    label: "Reach 25 WPM",
                    check: (stats) => stats.bestWPM >= 25,
                    progress: (stats) => ({ current: stats.bestWPM, target: 25, unit: "WPM" })
                },
                {
                    id: "qso_1",
                    label: "Complete 1 QSO",
                    check: (stats) => stats.totalQSOs >= 1,
                    progress: (stats) => ({ current: stats.totalQSOs || 0, target: 1, unit: "QSOs" })
                }
            ]
        },
        recommendedSkills: ["Reach 25 WPM", "Complete 1 QSO"],
        backgroundColor: "#2c3e50",
        accentColor: "#f1c40f",
        icon: "✈️",
        iconImage: "/assets/timeline/icons/era5.png",
        totalEvents: 6,
        events: [
            {
                id: "era5_event1",
                eraNumber: 5,
                eventNumber: 1,
                title: "First Radio‑Assisted Trans‑Atlantic Flight",
                date: "June 14, 1919",
                location: "New York → London",
                message: "VICKERS VIMY‑RADIO‑ASSIST",
                audioProfile: "cw_tubes",
                difficulty: 3,
                historicalContext: "After WW‑I, the British Vickers Vimy bomber (piloted by John Alcock and Arthur Brown) became the first aircraft to cross the Atlantic with a **radio transmitter** on board.  The crew used a 200‑watt CW set to send Morse‑coded status updates to ground stations in New York, Newfoundland and London, proving that long‑range aviation could stay in contact via radio.  Their success spurred the rapid growth of airmail and commercial aviation in the 1920s.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Radio‑Assisted Flight",
                            description: "Copy the CW status beacons sent during the crossing",
                            speed: 22,
                            qsb: true,
                            noise: true
                        }
                    ],
                    hints: [
                        "Aircraft type Vickers Vimy",
                        "First trans‑Atlantic radio‑assisted flight",
                        "Pilots Alcock & Brown"
                    ],
                    passingAccuracy: 85
                },
                artifacts: [
                    {
                        id: "artifact_vimy_radio",
                        name: "Vimy Radio Set",
                        description: "The 200‑W CW transmitter used on the historic flight",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era5/vimy_radio.png"
                    }
                ],
                rewards: { xp: 950 },
                triviaLinks: [127], // Vickers Vimy
            },
            {
                id: "era5_event2",
                eraNumber: 5,
                eventNumber: 2,
                title: "First Transatlantic Amateur Signal",
                date: "December 11, 1921",
                location: "Greenwich, CT -> Ardrossan, Scotland",
                message: "TEST 1BCG",
                audioProfile: "cw_tubes",
                difficulty: 3,
                historicalContext: "In 1921, experts believed shortwaves were useless for long distance. Amateurs proved them wrong. Station 1BCG sent a message across the Atlantic to Scotland using 990 watts, launching the era of worldwide amateur communication.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: DXpedition",
                            description: "Copy the faint historic signal",
                            speed: 20,
                            qsb: true
                        }
                    ],
                    hints: ["The word 'TEST'", "Station callsign '1BCG'"],
                    passingAccuracy: 85
                },
                artifacts: [
                    {
                        id: "artifact_1bcg_qsl",
                        name: "1BCG QSL Card",
                        description: "Confirmation of the first transatlantic amateur message",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era5/1bcg_qsl.png"
                    }
                ],
                rewards: { xp: 600 },
                triviaLinks: [128], // 1BCG
            },
            {
                id: "era5_event3",
                eraNumber: 5,
                eventNumber: 3,
                title: "The Norge Airship Over the North Pole",
                date: "May 12, 1926",
                location: "North Pole (Arctic)",
                message: "NORGEE‑SOS",
                audioProfile: "cw_tubes",
                difficulty: 3,
                historicalContext: "The Norge, a semi‑rigid airship piloted by Roald Amundsen, Umberto Nobile and Lincoln Ellis, became the first aircraft to fly over the North Pole.  A small CW transmitter on board sent a brief SOS when the envelope was damaged by ice.  The call was heard by a chain of Arctic ham stations, showing how Morse could be a lifeline even in the most remote environments.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Arctic SOS",
                            description: "Copy the distress call from the Norge",
                            speed: 18,
                            qsb: true,
                            noise: true
                        }
                    ],
                    hints: ["Airship", "North Pole", "SOS"],
                    passingAccuracy: 80
                },
                artifacts: [
                    {
                        id: "artifact_norge_fragment",
                        name: "Norge Airship Fabric",
                        description: "A preserved piece of the historic airship envelope",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era5/norge_fragment.png"
                    }
                ],
                rewards: { xp: 900 },
                triviaLinks: [129], // Norge airship
            },
            {
                id: "era5_event4",
                eraNumber: 5,
                eventNumber: 4,
                title: "Airship Italia Rescue",
                date: "May 25, 1928",
                location: "Arctic Ocean",
                message: "KRA KRA SOS ITALIA NOBILE",
                audioProfile: "cw_tubes",
                difficulty: 3,
                historicalContext: "When the airship Italia crashed on the Arctic ice, the survivors were stranded. Their only hope was a small emergency field radio. A young Russian amateur radio operator (Schmidt) heard their faint SOS thousands of miles away, coordinating their rescue.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Arctic SOS",
                            description: "Decode the weak distress call through static",
                            speed: 18,
                            qsb: true,
                            noise: true
                        }
                    ],
                    hints: ["Repeating SOS", "Name of the ship 'ITALIA'", "Commander's name 'NOBILE'"],
                    passingAccuracy: 80
                },
                artifacts: [
                    {
                        id: "artifact_field_radio",
                        name: "Biagio Field Radio",
                        description: "The small battery-powered radio that saved the expedition",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era5/field_radio.png"
                    }
                ],
                rewards: { xp: 700 },
                triviaLinks: [130], // Airship Italia
            },
            {
                id: "era5_event5",
                eraNumber: 5,
                eventNumber: 5,
                title: "Earhart’s Atlantic Attempt",
                date: "June 17, 1928",
                location: "New York → Ireland",
                message: "EARHART‑OVER‑ATLANTIC‑SOS‑WE‑NEED‑HELP",
                audioProfile: "cw_tubes",
                difficulty: 4,
                historicalContext: "Just a year after Lindbergh's historic solo transatlantic flight, Amelia Earhart tried to become the first woman to fly nonstop across the Atlantic.  Her aircraft carried a compact 5‑watt CW transmitter that sent a distress‑call when she ran out of fuel.  The call was picked up by a network of amateur stations, illustrating how CW could act as a life‑saving “air‑to‑ground” link.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: SOS Over Ocean",
                            description: "Copy the SOS that was heard by many hams",
                            speed: 24,
                            qsb: true,
                            noise: true
                        }
                    ],
                    hints: ["SOS pattern", "Female aviator", "Atlantic crossing"],
                    passingAccuracy: 80
                },
                artifacts: [
                    {
                        id: "artifact_earhart_radio",
                        name: "Earhart’s Portable Radio",
                        description: "Miniature CW set used on the trans‑Atlantic attempt",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era5/earhart_radio.png"
                    }
                ],
                rewards: { xp: 950 },
                triviaLinks: [131], // Earhart's Atlantic attempt
            },
            {
                id: "era5_event6",
                eraNumber: 5,
                eventNumber: 6,
                title: "Amelia Earhart's Last Flight",
                date: "July 2, 1937",
                location: "Pacific Ocean",
                message: "KHAQQ TO ITASCA WE ARE ON LINE 157 337",
                audioProfile: "cw_tubes",
                difficulty: 4,
                historicalContext: "Aviation pioneer Amelia Earhart disappeared over the Pacific. Her Lockheed Electra (Callsign KHAQQ) carried a radio, but she couldn't hear the coast guard cutter Itasca. Her final transmissions were frantic reports of her position line, heard but not triangulated.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Lost at Sea",
                            description: "Copy the final position report",
                            speed: 22,
                            qsb: true
                        }
                    ],
                    hints: ["Callsign KHAQQ", "Mention of 'ITASCA'", "Compass headings"],
                    passingAccuracy: 90
                },
                artifacts: [
                    {
                        id: "artifact_earhart_newspaper",
                        name: "Earhart Missing Headline",
                        description: "Front page of the Los Angeles Times declaring Amelia Earhart missing at sea",
                        rarity: "legendary",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era5/earhart_missing.png"
                    }
                ],
                rewards: {
                    xp: 800,
                    nextEraHint: "Era 6: World War II"
                },
                triviaLinks: [132], // Earhart's last flight
            },
            {
                id: "era5_event_pedal",
                eraNumber: 5,
                eventNumber: 7,
                title: "The Flying Doctor",
                date: "1928",
                location: "Cloncurry, Australia",
                message: "DOCTOR NEEDED AT REMOTE STATION",
                audioProfile: "cw_tubes",
                difficulty: 2,
                historicalContext: "In the vast Australian Outback, there were no phones. Reverend John Flynn and engineer Alfred Traeger invented the **Pedal Radio**, a human-powered generator that allowed isolated cattle stations to tap out Morse code to call for a doctor. It changed life in the remote outback forever.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Emergency Call", description: "Decode the pedal-powered plea", speed: 18, qsb: true },
                        { id: "visual", name: "Visual: Signal Lamp", description: "Remote station signaling", visualAnimation: true, visualType: "lamp", speed: 12 }
                    ],
                    hints: ["Medical professional", "Location description", "Reason for call"],
                    passingAccuracy: 80
                },
                artifacts: [
                    { id: "artifact_pedal_radio", name: "Traeger Pedal Radio", description: "The revolutionary pedal-powered wireless set", rarity: "rare", collectionMethod: "event_completion", imageFile: "/assets/timeline/era5/pedal_radio.png" }
                ],
                rewards: { xp: 700 }
            },
            {
                id: "era5_event_byrd",
                eraNumber: 5,
                eventNumber: 8,
                title: "Byrd Over South Pole",
                date: "November 29, 1929",
                location: "Antarctica",
                message: "MY CALCULATIONS INDICATE WE HAVE REACHED POLE",
                audioProfile: "cw_tubes",
                difficulty: 3,
                historicalContext: "Explorer Richard E. Byrd flew a Ford Trimotor over the South Pole. Unlike previous explorers who vanished for years, Byrd stayed in constant CW contact with his base 'Little America' and the outside world, making it the first 'media event' exploration.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Polar Transmission", description: "Decode the historic report", speed: 20, qsb: true, noise: true }
                    ],
                    hints: ["What he is doing (Calculations)", "Destination reached", "Geographic location"],
                    passingAccuracy: 85
                },
                artifacts: [
                    { id: "artifact_byrd_compass", name: "Byrd's Sun Compass", description: "Precision navigation instrument used near the pole", rarity: "epic", collectionMethod: "event_completion", imageFile: "/assets/timeline/era5/byrd_compass.png" }
                ],
                rewards: { xp: 850 }
            },
            {
                id: "era5_event_spark_ban",
                eraNumber: 5,
                eventNumber: 9,
                title: "The Spark Ban",
                date: "1927",
                location: "International",
                message: "SPARK TRANSMISSIONS PROHIBITED USE VACUUM TUBES",
                audioProfile: "spark",
                difficulty: 3,
                historicalContext: "For decades, the messy, broad-spectrum 'spark' transmitters clogged the airwaves. The International Radiotelegraph Convention of 1927 finally banned them in favor of clean, continuous wave (CW) signals generated by vacuum tubes, marking the end of the 'pioneer' era.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Regulation", description: "Decode the new law", speed: 18 },
                        { id: "visual", name: "Visual: Spark Gap", description: "The last spark transmission", visualAnimation: true, visualType: "spark", speed: 15 }
                    ],
                    hints: ["Forbidden technology", "New technology required", "Action taken"],
                    passingAccuracy: 75
                },
                artifacts: [
                    { id: "artifact_vacuum_tube", name: "1927 Triode Tube", description: "The component that replaced the noisy spark gap", rarity: "common", collectionMethod: "event_completion", imageFile: "/assets/timeline/era5/vacuum_tube.png" }
                ],
                rewards: { xp: 600 }
            }
        ]
    },

    // ============================================
    // Era 6: 1939-1945 - WWII
    // ============================================
    {
        id: 6,
        number: 6,
        title: "World War II",
        period: "1939-1945",
        subtitle: "Espionage, Enigma, and Victory",
        description: "Secret agents, resistance networks, and encrypted messages during history's greatest conflict.",
        theme: "war_room",
        unlockType: "hard",
        unlockRequirements: {
            type: "any",
            count: 2,
            requirements: [
                {
                    id: "qso_wpm_25",
                    label: "Complete 1 QSO + Reach 25 WPM",
                    check: (stats) => stats.totalQSOs >= 1 && stats.bestWPM >= 25,
                    progress: (stats) => ({ current: stats.totalQSOs, target: 1, unit: "QSOs" })
                },
                {
                    id: "modes_5",
                    label: "Try 5 different game modes",
                    check: (stats) => stats.modesExplored >= 5,
                    progress: (stats) => ({ current: stats.modesExplored, target: 5, unit: "modes" })
                },
                {
                    id: "qsb_training",
                    label: "Copy with QSB enabled",
                    check: (stats) => stats.qsbSessions >= 1,
                    progress: (stats) => ({ current: stats.qsbSessions, target: 1, unit: "session" })
                },
            ]
        },
        recommendedSkills: ["Reach 25 WPM", "Complete 1 QSO", "Try 5 game modes", "Practice with QSB"],
        backgroundColor: "#1c1c1c",
        accentColor: "#d32f2f",
        icon: "🕵️",
        iconImage: "/assets/timeline/icons/era6.png",
        totalEvents: 6,
        events: [
            {
                id: "era6_event1",
                eranumber: 6,
                eventNumber: 1,
                title: "Pearl Harbor",
                date: "December 7, 1941",
                location: "Oahu, Hawaii",
                message: "AIR RAID PEARL HARBOR X THIS IS NO DRILL",
                audioProfile: "cw_tubes",
                difficulty: 3,
                historicalContext: "The most famous military telegram of the 20th century. Sent by Admiral Patrick Bellinger from Ford Island minutes after the first bombs fell. It was broadcast unencrypted to every ship and base in the Pacific, shocking the world.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: The Warning",
                            description: "Decode the infamous alert",
                            speed: 20,
                        },
                        {
                            id: "visual",
                            name: "Visual: Signal Tower",
                            description: "Receive light signals from the tower",
                            visualAnimation: true,
                            visualType: "lamp",
                            speed: 15,
                        }
                    ],
                    hints: [
                        "Starts with the type of attack",
                        "Mentions the location",
                        "Confirms it is 'NO DRILL'"
                    ],
                    passingAccuracy: 90,
                },

                artifacts: [
                    {
                        id: "artifact_navy_key",
                        name: "US Navy Speed Key",
                        description: "Standard issue 'bug' key used by rapid naval operators",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era6/navy_bug.png"
                    }
                ],

                quiz: [
                    {
                        question: "What does 'X' mean in the message?",
                        options: ["Kiss", "Stop/Period", "Unknown", "Secret"],
                        correctAnswer: 1,
                        explanation: "In telegraphy, 'X' or 'STOP' is often used as a period or separator between sentences.",
                    }
                ],
                rewards: { xp: 500 },
                triviaLinks: [133], // Pearl Harbor
            },
            {
                id: "era6_event2",
                eranumber: 6,
                eventNumber: 2,
                title: "Battle of Midway (The Trap)",
                date: "May 1942",
                location: "Midway Atoll",
                message: "AF IS SHORT OF WATER",
                audioProfile: "cw_tubes",
                difficulty: 4,
                historicalContext: "US Intelligence cracked the Japanese Naval Code (JN-25) but didn't know where objective 'AF' was. They tricked the Japanese by sending a fake unencrypted message that Midway's water plant was broken. When Japanese radio reported 'AF is short of water', the target was confirmed as Midway.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Intel Intercept",
                            description: "Decode the enemy report",
                            speed: 22,
                        }
                    ],
                    hints: [
                        "Two letter code for the target (AF)",
                        "The fake problem reported",
                        "Vital resource (WATER)"
                    ],
                    passingAccuracy: 85,
                },

                artifacts: [
                    {
                        id: "artifact_jn25_chart",
                        name: "JN-25 Code Grid",
                        description: "Fragment of the Japanese naval code reconstruction table",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era6/jn25_chart.png"
                    }
                ],
                rewards: { xp: 600 },
                triviaLinks: [134], // Battle of Midway
            },
            {
                id: "era6_event3",
                eranumber: 6,
                eventNumber: 3,
                title: "Pacific Coastwatchers",
                date: "August 1942",
                location: "Solomon Islands",
                message: "OBJECTS MOVING TOWARD GUADALCANAL",
                audioProfile: "cw_tubes",
                difficulty: 3,
                historicalContext: "Lonely 'Coastwatchers' hid on islands behind enemy lines, using heavy teleradios to report Japanese ship and aircraft movements. Their warnings gave US forces crucial time to launch interceptors during the Guadalcanal campaign.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Jungle Report",
                            description: "Report the enemy fleet",
                            speed: 18,
                        },
                        {
                            id: "visual",
                            name: "Visual: Hidden Signal",
                            description: "Low power transmission",
                            visualAnimation: true,
                            visualType: "lamp",
                            speed: 12,
                        }
                    ],
                    hints: [
                        "What is being seen (OBJECTS)",
                        "Direction of travel",
                        "Destination island"
                    ],
                    passingAccuracy: 80,
                },

                artifacts: [
                    {
                        id: "artifact_teleradio",
                        name: "AWA Teleradio 3B",
                        description: "Heavy, tropicalized radio set used by Coastwatchers",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era6/teleradio.png"
                    }
                ],
                rewards: { xp: 550 },
                triviaLinks: [135], // Coastwatchers
            },
            {
                id: "era6_event4",
                eranumber: 6,
                eventNumber: 4,
                title: "French Resistance (BBC)",
                date: "June 5, 1944",
                location: "Occupied France",
                message: "THE DICE ARE ON THE CARPET",
                audioProfile: "cw_tubes",
                difficulty: 5,
                historicalContext: "The BBC broadcast hundreds of 'personal messages' to the Resistance. Most were nonsense to Germans, but specific phrases signaled sabotage teams to blow up rail lines before D-Day. 'Les dés sont sur le tapis' (The dice are on the carpet) was a call to cut telephone networks.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Radio Londres",
                            description: "Decode the secret activation phrase",
                            speed: 24,
                        },
                        {
                            id: "standard",
                            name: "Standard: QSB (Fading)",
                            description: "Weak signal from London",
                            qsb: true,
                            speed: 20
                        }
                    ],
                    hints: [
                        "A phrase about gambling",
                        "Mention of 'DICE'",
                        "Where they are placed (CARPET)"
                    ],
                    passingAccuracy: 90,
                },

                artifacts: [
                    {
                        id: "artifact_soe_pencil",
                        name: "Time Pencil Detonator",
                        description: "Chemical delay fuse used by Resistance saboteurs",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era6/time_pencil.png"
                    }
                ],
                rewards: { xp: 700 },
                triviaLinks: [136], // French Resistance
            },
            {
                id: "era6_event5",
                eranumber: 6,
                eventnumber: 6,
                title: "Bletchley Park",
                date: "1944",
                location: "Station X, UK",
                message: "WEATHER CLEAR OVER NORMANDY",
                audioProfile: "cw_tubes",
                difficulty: 4,
                historicalContext: "The outcome of D-Day depended on the weather. Intercepting German reports via Enigma confirming they did NOT expect an invasion due to storms allowed the Allies to launch the attack when a small window of clear weather appeared.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Enigma Decrypt",
                            description: "Read the decoded intelligence",
                            speed: 25,
                        },
                        {
                            id: "visual_enigma",
                            name: "Visual: Enigma Machine",
                            description: "Watch the Enigma lampboard light up",
                            visualAnimation: true,
                            visualType: "enigma",
                            speed: 18,
                        },
                        {
                            id: "expert",
                            name: "Expert: Raw Intercept",
                            description: "Copy encrypted 5-letter code groups like a Y-Station operator",
                            message: "ENIGM ACYPH ERXXX",
                            speed: 25,
                        }
                    ],
                    hints: [
                        "Atmospheric conditions",
                        "Status (CLEAR)",
                        "Location of invasion"
                    ],
                    passingAccuracy: 95,
                },

                artifacts: [
                    {
                        id: "artifact_enigma_rotor",
                        name: "Enigma Rotor (M4)",
                        description: "A cryptographic rotor from a German Navy Enigma machine",
                        rarity: "legendary",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era6/enigma_rotor.png"
                    }
                ],
                rewards: {
                    xp: 1000,
                    themes: ["war_room"],
                    achievements: ["enigma_cracked"]
                },
                triviaLinks: [137], // Bletchley Park
            },
            {
                id: "era6_event6",
                eranumber: 6,
                eventnumber: 7,
                title: "Victory in Pacific",
                date: "September 2, 1945",
                location: "USS Missouri, Tokyo Bay",
                message: "WAR OVER SIGNED TOKYO BAY",
                audioProfile: "cw_tubes",
                difficulty: 2,
                historicalContext: "The final official message of WWII. The surrender documents were signed aboard the USS Missouri. The news was flashed around the globe, ending six years of conflict.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Fleet Broadcast",
                            description: "Decode the end of the war",
                            speed: 15,
                        }
                    ],
                    hints: [
                        "Status of the conflict",
                        "Action taken (SIGNED)",
                        "Location of the ship"
                    ],
                    passingAccuracy: 100,
                },

                artifacts: [
                    {
                        id: "artifact_victory_pen",
                        name: "Surrender Pen Replica",
                        description: "One of the pens used to sign the Instrument of Surrender",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era6/victory_pen.png"
                    }
                ],
                rewards: {
                    xp: 2000, // Huge XP for ending Era 6
                    nextEraHint: "Era 7: Space Age (Coming Soon)"
                },
                triviaLinks: [138], // Victory in Pacific
            },
            {
                id: "era6_event_bismarck",
                eraNumber: 6,
                eventNumber: 8,
                title: "The Hunt for the Bismarck",
                date: "May 26, 1941",
                location: "North Atlantic",
                message: "SHIP MAKING BEST SPEED TO BREST",
                audioProfile: "cw_tubes",
                difficulty: 4,
                historicalContext: "The mighty German battleship **Bismarck** had sunk the HMS Hood and vanished into the Atlantic fog. Desperate to reach safety in France, Admiral Lütjens broke radio silence to send a long status report to Berlin. British 'Y-Stations' intercepted the transmission, triangulated the position, and directed the Royal Navy to the target.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Long Transmission", description: "Decode the fatal mistake", speed: 20 },
                        { id: "visual", name: "Visual: Direction Finder", description: "Triangulate the signal", visualAnimation: true, visualType: "lamp", speed: 15 }
                    ],
                    hints: ["Destination city (BREST)", "Status of the ship", "Speed description"],
                    passingAccuracy: 85
                },
                artifacts: [
                    { id: "artifact_bismarck_map", name: "Kriegsmarine Grid Map", description: "Naval chart showing the Bismarck's desperate course", rarity: "rare", collectionMethod: "event_completion", imageFile: "/assets/timeline/era6/bismarck_map.png" }
                ],
                rewards: { xp: 750 }
            },
            {
                id: "era6_event_madeleine",
                eraNumber: 6,
                eventNumber: 9,
                title: "Agent Madeleine",
                date: "1943",
                location: "Paris, France",
                message: "SCHOOL IS CLOSING",
                audioProfile: "cw_tubes",
                difficulty: 5,
                historicalContext: "Noor Inayat Khan (Codename: Madeleine) was the first female wireless operator sent into occupied France. Armed with a compact suitcase radio (B2 Spy Set), she moved constantly to avoid detection. Even when her network collapsed, she refused to abandon her post, remaining the sole radio link for the Paris Resistance.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Clandestine", description: "Decode the final warning", speed: 25, qsb: true },
                        { id: "visual", name: "Visual: Signal Lamp", description: "Low light transmission", visualAnimation: true, visualType: "lamp", speed: 20 }
                    ],
                    hints: ["A phrase about an institution", "Action (CLOSING)", "Code for 'network compromised'"],
                    passingAccuracy: 90
                },
                artifacts: [
                    { id: "artifact_spy_radio", name: "B2 Spy Radio Set", description: "The 'suitcase set' used by SOE agents behind enemy lines", rarity: "epic", collectionMethod: "event_completion", imageFile: "/assets/timeline/era6/spy_radio.png" }
                ],
                rewards: { xp: 900 }
            },
            {
                id: "era6_event_dambusters",
                eraNumber: 6,
                eventNumber: 10,
                title: "The Dambusters",
                date: "May 17, 1943",
                location: "Ruhr Valley, Germany",
                message: "GONER 79A GONER",
                audioProfile: "cw_tubes",
                difficulty: 4,
                historicalContext: "617 Squadron's audacious raid on the German dams required precise low-level flying. The success of the attack on the Möhne Dam was signaled by the code word 'GONER' sent via key by the wireless operator aboard the victorious Lancaster bomber.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Bomber Comms", description: "Decode the success signal", speed: 22, noise: true }
                    ],
                    hints: ["Code word for success", "Repeated twice", "Target ID"],
                    passingAccuracy: 85
                },
                artifacts: [
                    { id: "artifact_r1155_receiver", name: "R1155 Receiver", description: "The radio receiver used in Lancaster bombers", rarity: "rare", collectionMethod: "event_completion", imageFile: "/assets/timeline/era6/r1155_receiver.png" }
                ],
                rewards: { xp: 800 }
            },
            {
                id: "era6_event_atlantic",
                eraNumber: 6,
                eventNumber: 11,
                title: "Battle of the Atlantic",
                date: "1942",
                location: "North Atlantic",
                message: "CONVOY SC 7 UNDER ATTACK",
                audioProfile: "cw_tubes",
                difficulty: 4,
                historicalContext: "The longest campaign of the war. U-Boat 'Wolf Packs' coordinated attacks on allied convoys using Enigma-encrypted radio signals. Allied escorts used Hydrophones (sonar) to listen for the screws of the submerged submarines and hunt them down.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Convoy Distress", description: "Decode the urgent SOS", speed: 20 },
                        { id: "visual", name: "Visual: Hydrophone", description: "Listen/Look for the sub signature", visualAnimation: true, visualType: "oscilloscope", speed: 12 }
                    ],
                    hints: ["Name of the Convoy", "Status (UNDER ATTACK)", "Urgent report"],
                    passingAccuracy: 85
                },
                artifacts: [
                    { id: "artifact_uboat_hydrophone", name: "U-Boat Hydrophone", description: "Underwater listening device used in the deadly game of cat and mouse", rarity: "epic", collectionMethod: "event_completion", imageFile: "/assets/timeline/era6/uboat_hydrophone.png" }
                ],
                rewards: { xp: 850 }
            }
        ],
    },

    // ============================================
    // Era 7: 1957-1980 - SPACE AGE
    // ============================================
    {
        id: 7,
        number: 7,
        title: "Space Age",
        period: "1957-1980",
        subtitle: "Satellites, Apollo, and Amateur Radio in Orbit",
        description: "Morse code reaches space as ham radio operators communicate via satellites and NASA uses CW for telemetry.",
        theme: "mission_control",
        unlockType: "hard",
        unlockRequirements: {
            type: "any",
            count: 3,
            requirements: [
                {
                    id: "wpm_30_echo",
                    label: "Reach 30 WPM + Echo training",
                    check: (stats) => stats.bestWPM >= 30 && stats.echoCompleted >= 1,
                    progress: (stats) => {
                        if (stats.bestWPM < 30) return { current: stats.bestWPM, target: 30, unit: "WPM" };
                        return { current: stats.echoCompleted || 0, target: 1, unit: "Echo Session" };
                    }
                },
                {
                    id: "skill_4",
                    label: "Reach Skill Level 4 (Head Copy)",
                    check: (stats) => stats.skillLevel >= 4,
                    progress: (stats) => ({ current: stats.skillLevel, target: 4, unit: "level" })
                },
                {
                    id: "achievements_5",
                    label: "Earn 5 achievements",
                    check: (stats) => stats.achievementCount >= 5,
                    progress: (stats) => ({ current: stats.achievementCount, target: 5, unit: "badges" })
                },
            ]
        },
        recommendedSkills: ["Reach 30 WPM", "Practice Echo Mode", "Reach Skill Level 4", "Earn 5 achievements"],
        backgroundColor: "#0a0e1a",
        accentColor: "#00bfff",
        icon: "🚀",
        iconImage: "/assets/timeline/icons/era7.png",
        totalEvents: 7,
        events: [
            {
                id: "era7_event1",
                eranumber: 7,
                eventNumber: 1,
                title: "Sputnik 1",
                date: "October 4, 1957",
                location: "Low Earth Orbit",
                message: "SPUTNIK ORBITING EARTH",
                audioProfile: "cw_tubes",
                difficulty: 2,
                historicalContext: "The Space Age began with a beep. Sputnik 1, the first artificial satellite, transmitted a rhythmic radio pulse that could be heard by amateur radio operators all over the world. It signaled that humanity had left the atmosphere.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Telemetry Intercept",
                            description: "Decode the simulated satellite ID",
                            speed: 18,
                            visualType: "oscilloscope"
                        },
                        {
                            id: "visual",
                            name: "Visual: Oscilloscope",
                            description: "Track the signal pulses",
                            visualAnimation: true,
                            visualType: "oscilloscope",
                            speed: 15,
                        }
                    ],
                    hints: ["Name of the satellite", "What is it doing?", "Target planet"],
                    passingAccuracy: 90,
                },
                artifacts: [
                    {
                        id: "artifact_sputnik",
                        name: "Sputnik 1 Satellite",
                        description: "The first artificial satellite, which transmitted a rhythmic Morse-like pulse from orbit.",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era7/sputnik.png"
                    }
                ],
                rewards: { xp: 800 },
                triviaLinks: [157], // Sputnik 1
            },
            {
                id: "era7_event2",
                eranumber: 7,
                eventNumber: 2,
                title: "Project OSCAR 1",
                date: "December 12, 1961",
                location: "Space",
                message: "HI HI",
                audioProfile: "cw",
                difficulty: 3,
                historicalContext: "Four years after Sputnik, amateur radio operators built their own satellite in a garage for $63. OSCAR 1 (Orbiting Satellite Carrying Amateur Radio) transmitted the Morse greeting 'HI' (four dots, two dots) endlessly for three weeks.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Satellite Downlink",
                            description: "Copy the first ham radio space signal",
                            speed: 22,
                            qsb: true
                        }
                    ],
                    hints: ["The amateur radio laugh", "Short greeting", "Repeated twice"],
                    passingAccuracy: 100,
                },
                artifacts: [
                    {
                        id: "artifact_oscar",
                        name: "OSCAR 1 Satellite",
                        description: "The first amateur radio satellite, built by hams and launched into orbit in 1961.",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era7/oscar.png"
                    }
                ],
                rewards: { xp: 850 },
                triviaLinks: [158], // Project OSCAR 1
            },
            {
                id: "era7_event3",
                eranumber: 7,
                eventNumber: 3,
                title: "Electronic Keyer",
                date: "c. 1960",
                location: "Ham Radio Station",
                message: "DITS AND DAHS AUTOMATIC",
                audioProfile: "cw",
                difficulty: 4,
                historicalContext: "The electronic keyer (and the dual-lever paddle) revolutionized CW. It generated perfect dots AND dashes automatically. This allowed for 'iambic' squeezing, creating a fluid, rolling style of sending.",
                decodingChallenge: {
                    modes: [
                        { id: "standard", name: "Standard: Iambic", description: "Decode the perfect timing", speed: 28 }
                    ],
                    hints: ["Dual components", "Both automatic", "Modern style"],
                    passingAccuracy: 90,
                },
                artifacts: [
                    { id: "artifact_bencher_paddle", name: "Bencher BY-1 Iambic Paddle", description: "The iconic chrome paddle that revolutionized high-speed Morse operation in the 1970s.", rarity: "rare", collectionMethod: "event_completion", imageFile: "/assets/timeline/era7/bencher_paddle.png" }
                ],
                rewards: { xp: 850 },
                triviaLinks: [159], // Electronic keyer
            },
            {
                id: "era7_event4",
                eranumber: 7,
                eventNumber: 4,
                title: "Message to Venus",
                date: "November 19, 1962",
                location: "EVPATORIA, CRIMEA",
                message: "MIR LENIN SSSR",
                audioProfile: "spark", // High power radar pulses
                difficulty: 3,
                historicalContext: "The first radio message ever beamed to another planet was sent by the Soviet Union. Directed at Venus using the new planetary radar, it consisted of three words in Morse code. It remains the earliest attempt at active SETI (Search for Extraterrestrial Intelligence).",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Planetary Radar",
                            description: "Decode the first message to the stars",
                            speed: 20,
                            visualType: "spark"
                        }
                    ],
                    hints: ["Russian word for Peace", "Soviet leader's name", "Country abbreviation"],
                    passingAccuracy: 90,
                },
                artifacts: [
                    {
                        id: "artifact_radar_glitch",
                        name: "Radar Pulse Printout",
                        description: "Visual record of the reflected signal from Venus",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era7/radar_glitch.png"
                    }
                ],
                rewards: { xp: 950 },
                triviaLinks: [160], // Message to Venus
            },
            {
                id: "era7_event5",
                eranumber: 7,
                eventNumber: 5,
                title: "Jeremiah Denton (POW)",
                date: "May 2, 1966",
                location: "Hanoi, Vietnam",
                message: "TORTURE",
                audioProfile: "cw",
                difficulty: 3,
                historicalContext: "U.S. Navy Commander Jeremiah Denton, held as a prisoner of war, was forced to participate in a televised propaganda interview. While answering questions, he stared into the bright lights and blinked his eyes in Morse code, spelling a key word regarding his ordeal.\\n\\nThis was the first confirmation to Naval Intelligence that American POWs were being mistreated.",

                decodingChallenge: {
                    modes: [
                        {
                            id: "visual",
                            name: "Visual: Blinking Eyes",
                            description: "Decode the blinking message",
                            speed: 6,
                            visualAnimation: true,
                            visualType: "blinking_eyes",
                            noAudio: true
                        },
                        {
                            id: "standard",
                            name: "Standard: Intelligence",
                            description: "Decode the raw report",
                            speed: 15,
                        }
                    ],
                    hints: [
                        "Watch the blinking pattern",
                        "Describes the prisoner's condition",
                        "Starts with T"
                    ],
                    passingAccuracy: 100,
                },

                artifacts: [
                    {
                        id: "artifact_pow_bracelet",
                        name: "POW/MIA Bracelet",
                        description: "A metal bracelet worn to remember prisoners of war",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era7/pow_bracelet.png"
                    }
                ],

                quiz: [
                    {
                        question: "How did Jeremiah Denton send his message?",
                        options: ["Hidden radio", "Blinking his eyes", "Tapping the table", "Writing in code"],
                        correctAnswer: 1,
                        explanation: "He used Morse code by blinking his eyes during a televised propaganda interview.",
                    }
                ],

                rewards: { xp: 1100 },
                triviaLinks: [161], // Jeremiah Denton
            },
            {
                id: "era7_event6",
                eranumber: 7,
                eventNumber: 6,
                title: "Apollo 11",
                date: "July 20, 1969",
                location: "Mare Tranquillitatis",
                message: "THE EAGLE HAS LANDED",
                audioProfile: "cw",
                difficulty: 4,
                historicalContext: "One of humanity's greatest achievements. While Neil Armstrong spoke these words, the technology that got them there relied on digital pulses and telemetry descended directly from Morse's first clicks.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Lunar Surface",
                            description: "Decode the historic landing announcement",
                            speed: 24,
                        }
                    ],
                    hints: ["Name of the module", "Action completed", "Famous phrase"],
                    passingAccuracy: 95,
                },
                artifacts: [
                    {
                        id: "artifact_lunar_plaque",
                        name: "Lunar Plaque Replica",
                        description: "We come in peace for all mankind",
                        rarity: "legendary",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era7/lunar_plaque.png"
                    }
                ],
                rewards: { xp: 1200, themes: ["mission_control"] },
                triviaLinks: [162], // Apollo 11
            },
            {
                id: "era7_event7",
                eranumber: 7,
                eventNumber: 7,
                title: "Arecibo Message",
                date: "1974",
                location: "Puerto Rico",
                message: "12345678910",
                audioProfile: "cw",
                difficulty: 5,
                historicalContext: "The Arecibo message was a binary interstellar radio message sent to the globular star cluster M13. Before the image, the message starts with a simple set of digits to establish a number system.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: SETI Transmission",
                            description: "Decode the number sequence",
                            speed: 28, // Fast!
                        },

                    ],
                    hints: ["It's a count", "Starts with 1", "Ends with 10"],
                    passingAccuracy: 100,
                },
                artifacts: [
                    {
                        id: "artifact_arecibo_message",
                        name: "Arecibo Message",
                        description: "The interstellar radio message sent to globular star cluster M13 in 1974.",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era7/arecibo_message.png"
                    }
                ],
                rewards: {
                    xp: 2000,
                    achievements: ["space_pioneer"],
                    nextEraHint: "Era 8: The Digital Age"
                },
                triviaLinks: [163], // Arecibo Message
            }
        ],
    },

    // ============================================
    // Era 8: 1980-2000 - DIGITAL AGE
    // ============================================
    {
        id: 8,
        number: 8,
        title: "Digital Age",
        period: "1980-2000",
        subtitle: "The End of an Era and a New Beginning",
        description: "As satellites and the internet took over, professional Morse code faded, but the amateur community transformed it into a high-speed competitive sport.",
        theme: "digital_modern",
        unlockType: "hard",
        unlockRequirements: {
            type: "any",
            count: 3,
            requirements: [
                {
                    id: "wpm_35_contest",
                    label: "Reach 35 WPM + Contest mode",
                    check: (stats) => stats.bestWPM >= 35 && stats.contestCompleted >= 1,
                    progress: (stats) => ({ current: stats.bestWPM, target: 35, unit: "WPM" })
                },
                {
                    id: "head_copy_10",
                    label: "Head copy 10 words",
                    check: (stats) => (stats.headCopyWords || 0) >= 10,
                    progress: (stats) => ({ current: stats.headCopyWords || 0, target: 10, unit: "words" })
                },
                {
                    id: "pileup",
                    label: "Complete pileup simulator",
                    check: (stats) => stats.pileupCompleted >= 1,
                    progress: (stats) => ({ current: stats.pileupCompleted ? 1 : 0, target: 1, unit: "pileup" })
                },
                {
                    id: "streak_3",
                    label: "Maintain 3-day streak",
                    check: (stats) => stats.currentStreak >= 3,
                    progress: (stats) => ({ current: stats.currentStreak, target: 3, unit: "days" })
                },
            ]
        },
        recommendedSkills: ["Reach 35 WPM", "Try Contest Mode", "Practice Head Copy", "Complete Pileup Simulator", "Maintain 3-day Streak"],
        backgroundColor: "#1a1a2e",
        accentColor: "#00ff41",
        icon: "💻",
        iconImage: "/assets/timeline/icons/era8.png",
        totalEvents: 13,
        events: [
            {
                id: "era8_event1",
                eranumber: 8,
                eventNumber: 1,
                title: "The Numbers Stations",
                date: "c. 1985",
                location: "Unknown Locations",
                location: "Unknown Locations",
                message: "39281 39281 55210 99401",
                audioProfile: "cw", // Creepy spy station
                difficulty: 3,
                historicalContext: "During the Cold War, mysterious shortwave stations broadcast endless streams of numbers in Morse code (and voice). These 'Numbers Stations' like 'The Lincolnshire Poacher' are believed to be coded instructions for spies, using One-Time Pads that are mathematically unbreakable.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Spy Decrypt",
                            description: "Copy the 5-digit code groups",
                            speed: 20,
                            visualType: "spark" // Gritty sound
                        }
                    ],
                    hints: ["Groups of 5 numbers", "Repeated preamble", "No plain text meanings"],
                    passingAccuracy: 100,
                },
                artifacts: [
                    {
                        id: "artifact_one_time_pad",
                        name: "Telegraphic Cipher Code",
                        description: "The Atlas Universal Travelers' and Business Telegraphic Cipher Code book, used for secure and brief communication.",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/one_time_pad.png"
                    }
                ],
                rewards: { xp: 1000 },
                triviaLinks: [164], // Numbers Stations
            },
            {
                id: "era8_event2",
                eraNumber: 8,
                eventNumber: 2,
                title: "CW Decoders Arrive",
                date: "1982",
                location: "Worldwide",
                message: "HELLO WORLD FROM COMPUTER",
                audioProfile: "cw",
                difficulty: 2,
                historicalContext: "The first affordable CW decoder software for early personal computers (Commodore 64, Apple II) revolutionized Morse code learning. For the first time, beginners could see Morse code visually on screen, making the learning process more accessible and less intimidating.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Digital Decode",
                            description: "Copy the computer's first CW message",
                            speed: 18,
                        }
                    ],
                    hints: ["Simple message", "Early computing", "Learning aid"],
                    passingAccuracy: 95,
                },
                artifacts: [
                    {
                        id: "artifact_cw_decoder",
                        name: "Early CW Decoder PCB",
                        description: "An early microcontroller-based CW decoder with an LCD character display.",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/cw_decoder.png"
                    }
                ],
                rewards: { xp: 800 },
                triviaLinks: [177], // CW Decoders
            },
            {
                id: "era8_event3",
                eraNumber: 8,
                eventNumber: 3,
                title: "Morse in Orbit",
                date: "April 1983",
                location: "Space Shuttle Challenger (STS-6)",
                message: "QSL FROM SPACE W5LFL",
                audioProfile: "cw",
                difficulty: 4,
                historicalContext: "Astronaut Owen Garriott (W5LFL) made history as the first to operate amateur radio CW from space aboard the Space Shuttle Challenger. He contacted ham radio operators worldwide, proving Morse code's versatility even in the ultimate frontier. His callsign became legendary in the ham community.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Space QSO",
                            description: "Copy the message from orbit",
                            speed: 22,
                            visualType: "spark" // Space conditions
                        }
                    ],
                    hints: ["From space!", "Astronaut callsign", "QSL = confirmation card"],
                    passingAccuracy: 98,
                },
                artifacts: [
                    {
                        id: "artifact_space_qsl",
                        name: "ISS QSL Card",
                        description: "Official ARISS (Amateur Radio on the International Space Station) confirmation card.",
                        rarity: "epic",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era8/space_qsl.png"
                    }
                ],
                rewards: { xp: 1300 },
                triviaLinks: [178], // Space CW
            },
            {
                id: "era8_event4",
                eraNumber: 8,
                eventNumber: 4,
                title: "Packet Radio Networks",
                date: "1985",
                location: "Worldwide",
                message: "KA2DEW-1 CONNECTED",
                audioProfile: "cw",
                difficulty: 3,
                historicalContext: "Amateur radio operators created the first wireless internet using packet radio and TCP/IP protocols, years before the public internet boom. Stations used Morse callsigns for identification, and this network became a precursor to modern wireless networking and emergency communication systems.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Packet Connect",
                            description: "Decode the connection handshake",
                            speed: 20,
                        }
                    ],
                    hints: ["Callsign with SSID", "CONNECTED status", "Network protocol"],
                    passingAccuracy: 95,
                },
                artifacts: [
                    {
                        id: "artifact_tnc",
                        name: "Kantronics KPC-3 TNC",
                        description: "The Kantronics PacketCommunicator 3 Plus, a classic Terminal Node Controller used for packet radio networks.",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/tnc.png"
                    }
                ],
                rewards: { xp: 900 },
                triviaLinks: [179], // Packet Radio
            },
            {
                id: "era8_event5",
                eraNumber: 8,
                eventNumber: 5,
                title: "QRP Revolution",
                date: "1988",
                location: "Worldwide",
                message: "5W TO THE WORLD",
                audioProfile: "cw",
                difficulty: 2,
                historicalContext: "The introduction of tiny, affordable QRP (low power) CW transceiver kits like the Rock-Mite revolutionized ham radio. Operators discovered they could contact the world with just 5 watts or less, proving that in CW, skill matters more than power. The QRP movement emphasized portability, emergency preparedness, and technical skill.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: QRP Contact",
                            description: "Copy the low-power transmission",
                            speed: 18,
                        }
                    ],
                    hints: ["QRP = low power", "5W = 5 watts", "Skill over power"],
                    passingAccuracy: 92,
                },
                artifacts: [
                    {
                        id: "artifact_rockmite",
                        name: "Rock-Mite Kit",
                        description: "Tiny CW transceiver that fits in an Altoids tin",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/rockmite.png"
                    }
                ],
                rewards: { xp: 700 },
                triviaLinks: [180], // QRP Movement
            },
            {
                id: "era8_event6",
                eraNumber: 8,
                eventNumber: 6,
                title: "Earthquake Emergency CW",
                date: "December 7, 1988",
                location: "Armenia, USSR",
                message: "SOS NEED HELP ARMENIA",
                audioProfile: "cw",
                difficulty: 4,
                historicalContext: "When a devastating 6.8 magnitude earthquake struck Armenia, killing over 25,000 people, all modern communications failed. Ham radio operators using Morse code became the only link to the outside world, coordinating rescue efforts and medical aid. This proved CW's irreplaceable value in disaster response.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Emergency Traffic",
                            description: "Copy the distress call through interference",
                            speed: 16,
                            visualType: "spark" // Emergency conditions
                        }
                    ],
                    hints: ["SOS = distress", "Emergency traffic", "Lives at stake"],
                    passingAccuracy: 100,
                },
                artifacts: [
                    {
                        id: "artifact_emergency_log",
                        name: "Emergency Net Log",
                        description: "Radio log from the Armenian earthquake emergency net",
                        rarity: "epic",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era8/emergency_log.png"
                    }
                ],
                rewards: { xp: 1400 },
                triviaLinks: [181], // Armenia Earthquake
            },
            {
                id: "era8_event7",
                eraNumber: 8,
                eventNumber: 7,
                title: "High-Speed Contesting",
                date: "July 1990",
                location: "Seattle, WRTC",
                message: "CQ TEST DE K1ABC K",
                audioProfile: "cw",
                difficulty: 5,
                historicalContext: "As professional use declined, Ham Radio contesting exploded. The World Radiosport Team Championship (WRTC), the 'Olympics of Ham Radio', held its first event in 1990. Elite operators communicate at 40, 50, or even 60 WPM.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: WRTC Qualifier",
                            description: "Copy the contest exchange at high speed",
                            speed: 35, // Very fast!
                        }
                    ],
                    hints: ["Standard call format", "CQ TEST (Call to contest)", "DE (From)"],
                    passingAccuracy: 95,
                },
                artifacts: [
                    {
                        id: "artifact_contest_trophy",
                        name: "WRTC Medallion",
                        description: "Award for elite operating skill",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/contest_trophy.png"
                    }
                ],
                rewards: { xp: 1100 },
                triviaLinks: [165], // High-Speed Contesting
            },
            {
                id: "era8_event8",
                eraNumber: 8,
                eventNumber: 8,
                title: "Desert Storm CW Backup",
                date: "January 1991",
                location: "Kuwait/Iraq",
                message: "SECURE HF CONFIRMED",
                audioProfile: "cw",
                difficulty: 4,
                historicalContext: "During Operation Desert Storm, despite having the most advanced military communications in history, U.S. forces still maintained CW capability as a secure HF backup. When satellite communications were jammed or compromised, Morse code on shortwave frequencies provided a reliable, low-tech alternative that proved impossible to disrupt.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Military Traffic",
                            description: "Copy the encrypted tactical message",
                            speed: 24,
                        }
                    ],
                    hints: ["HF = High Frequency", "Secure backup", "Military brevity"],
                    passingAccuracy: 98,
                },
                artifacts: [
                    {
                        id: "artifact_military_key",
                        name: "Field Telegraph Key",
                        description: "Ruggedized military Morse key used in Desert Storm",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/military_key.png"
                    }
                ],
                rewards: { xp: 1200 },
                triviaLinks: [182], // Gulf War CW
            },
            {
                id: "era8_event9",
                eraNumber: 8,
                eventNumber: 9,
                title: "Lower Code Requirements",
                date: "1991",
                location: "USA, FCC",
                message: "NEW RULES 13 WPM OK",
                audioProfile: "cw",
                difficulty: 2,
                historicalContext: "The FCC reduced the Morse code requirement from 20 WPM to 13 WPM for Extra Class and Amateur Extra licenses, making advanced ham radio licenses more accessible. This sparked debate: some saw it as lowering standards, others as removing barriers to amateur radio participation. The change reflected the declining commercial use of Morse.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: License News",
                            description: "Copy the regulatory announcement",
                            speed: 13, // The new requirement!
                        }
                    ],
                    hints: ["13 WPM standard", "License requirement", "FCC ruling"],
                    passingAccuracy: 90,
                },
                artifacts: [
                    {
                        id: "artifact_new_license",
                        name: "1991 License Certificate",
                        description: "Ham radio license under the new 13 WPM requirement",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/new_license.png"
                    }
                ],
                rewards: { xp: 600 },
                triviaLinks: [183], // Code Requirement Changes
            },
            {
                id: "era8_event10",
                eraNumber: 8,
                eventNumber: 10,
                title: "The Last Cry",
                date: "January 31, 1997",
                location: "French Navy HQ",
                message: "CALLING ALL THIS IS OUR LAST CRY BEFORE OUR ETERNAL SILENCE",
                audioProfile: "cw",
                difficulty: 3,
                historicalContext: "The French Navy officially ceased Morse code monitoring on this date. Their final transmission was a poetic farewell that marked the emotional end of an era for maritime sailors.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Maritime Farewell",
                            description: "Decode the final official message",
                            speed: 20,
                        }
                    ],
                    hints: ["Poetic phrasing", "Eternal Silence", "Final call"],
                    passingAccuracy: 98,
                },
                artifacts: [
                    {
                        id: "artifact_final_log",
                        name: "Final Watch Log",
                        description: "Logbook entry marking the end of the watch",
                        rarity: "legendary",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era8/final_log.png"
                    }
                ],
                rewards: { xp: 1200 },
                triviaLinks: [166], // The Last Cry
            },
            {
                id: "era8_event11",
                eraNumber: 8,
                eventNumber: 11,
                title: "Automated CW Decoding",
                date: "1999",
                location: "Worldwide",
                message: "ROBOT COPY WA7BNM",
                audioProfile: "cw",
                difficulty: 3,
                historicalContext: "The first distributed CW monitoring network using automatic decoders (CW Skimmer, Reverse Beacon Network) emerged, creating a global system of robot listeners that could spot weak CW signals. This technology revolutionized DXing and contesting by providing real-time propagation data.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Robot Decode",
                            description: "Copy what the robots hear",
                            speed: 25,
                        }
                    ],
                    hints: ["Automated monitoring", "Reverse beacon", "Global network"],
                    passingAccuracy: 95,
                },
                artifacts: [
                    {
                        id: "artifact_skimmer",
                        name: "CW Skimmer Interface",
                        description: "A digital waterfall display from CW Skimmer, showing real-time automated decoding of multiple Morse signals.",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/skimmer.png"
                    }
                ],
                rewards: { xp: 1000 },
                triviaLinks: [184], // CW Skimmer
            },
            {
                id: "era8_event12",
                eraNumber: 8,
                eventNumber: 12,
                title: "The Great Morse Debate",
                date: "1999",
                location: "IARU Conference",
                message: "FUTURE OF CW UNCERTAIN",
                audioProfile: "cw",
                difficulty: 3,
                historicalContext: "The International Amateur Radio Union (IARU) began serious discussions about removing the Morse code requirement for ham radio licenses worldwide. This sparked passionate debate: traditionalists argued it preserved skill and heritage, while reformers claimed it was an outdated barrier. The decision would come in 2003.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Conference Message",
                            description: "Copy the controversial announcement",
                            speed: 20,
                        }
                    ],
                    hints: ["Requirement debate", "IARU decision", "Traditional vs modern"],
                    passingAccuracy: 92,
                },
                artifacts: [
                    {
                        id: "artifact_iaru_memo",
                        name: "IARU Conference Minutes",
                        description: "Official memo discussing Morse requirement removal",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/iaru_memo.png"
                    }
                ],
                rewards: { xp: 900 },
                triviaLinks: [185], // Morse Requirement Debate
            },
            {
                id: "era8_event13",
                eraNumber: 8,
                eventNumber: 13,
                title: "End of Commercial CW",
                date: "July 12, 1999",
                location: "San Francisco, KPH",
                message: "WHAT HATH GOD WROUGHT",
                audioProfile: "cw",
                difficulty: 2,
                historicalContext: "The United States finally discontinued commercial Morse code requirements. The final message sent by the famous coastal station KPH repeated Samuel Morse's first message, 155 years later, bringing the age of commercial telegraphy to a symbolic close.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: The Final 73",
                            description: "Decode the closing ceremony message",
                            speed: 22,
                        }
                    ],
                    hints: ["Same as the first message involved", "Bible quote", "Samuel Morse's words"],
                    passingAccuracy: 100,
                },
                artifacts: [
                    {
                        id: "artifact_golden_key_end",
                        name: "Commemorative Key",
                        description: "Marking the final commercial transmission",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era8/golden_key_end.png"
                    }
                ],
                rewards: {
                    xp: 2000,
                    nextEraHint: "Era 9: Modern Era"
                },
                triviaLinks: [167], // End of Commercial CW
            },
        ],
    },

    // ============================================
    // Era 9: 2000-PRESENT - MODERN ERA
    // ============================================
    {
        id: 9,
        number: 9,
        title: "Modern Era",
        period: "2000-Present",
        subtitle: "The Living Heritage of CW",
        description: "Despite digital modes, Morse code thrives in amateur radio, DXpeditions, SOTA/POTA, and emergency communications.",
        theme: "modern_sdr",
        unlockType: "hard",
        unlockRequirements: {
            type: "any",
            count: 4,
            requirements: [
                {
                    id: "wpm_40_all_skills",
                    label: "Reach 40 WPM + All skill levels",
                    check: (stats) => stats.bestWPM >= 40 && stats.skillLevel >= 5,
                    progress: (stats) => ({ current: stats.bestWPM, target: 40, unit: "WPM" })
                },
                {
                    id: "icr_streak",
                    label: "Master Head Copy + 7-day streak",
                    check: (stats) => stats.icrMastered && stats.currentStreak >= 7,
                    progress: (stats) => ({ current: stats.currentStreak, target: 7, unit: "days" })
                },
                {
                    id: "sessions_100",
                    label: "Complete 100 total sessions",
                    check: (stats) => stats.totalSessions >= 100,
                    progress: (stats) => ({ current: stats.totalSessions, target: 100, unit: "sessions" })
                },
                {
                    id: "achievements_10",
                    label: "Earn 10 achievements",
                    check: (stats) => stats.achievementCount >= 10,
                    progress: (stats) => ({ current: stats.achievementCount, target: 10, unit: "badges" })
                },
            ]
        },
        recommendedSkills: ["Reach 40 WPM", "Master All Skills", "Master Head Copy", "Maintain 7-day Streak", "Complete 100 Sessions", "Earn 10 Achievements"],
        backgroundColor: "#0f0f0f",
        accentColor: "#ff6b35",
        icon: "🌐",
        iconImage: "/assets/timeline/icons/era9.png",
        totalEvents: 6,
        events: [
            {
                id: "era9_event1",
                eranumber: 9,
                eventNumber: 1,
                title: "The @ Symbol",
                date: "May 2004",
                location: "ITU Headquarters",
                location: "ITU Headquarters",
                message: "AC6V@AOL.COM",
                audioProfile: "cw",
                difficulty: 2,
                historicalContext: "In the first official change to the Morse code set since World War II, the International Telecommunication Union (ITU) officially added the '@' symbol (AC: .--.-.) to facilitate the sending of email addresses.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Email Address",
                            description: "Decode with the new character",
                            speed: 20,
                        }
                    ],
                    hints: ["Common email format", "Includes the new symbol", "ISP name"],
                    passingAccuracy: 100,
                },
                artifacts: [
                    {
                        id: "artifact_itu_doc",
                        name: "ITU Cultural Heritage Doc",
                        description: "Official 2012 ITU document detailing the cultural heritage and preservation of Morse code.",
                        rarity: "common",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era9/itu_doc.png"
                    }
                ],
                rewards: { xp: 1300 },
                triviaLinks: [168], // The @ Symbol
            },
            {
                id: "era9_fcc_no_code",
                eraNumber: 9,
                eventNumber: 1.5,
                title: "FCC Drops Morse Requirement",
                date: "February 23, 2007",
                location: "Washington D.C., USA",
                message: "NO CODE REQUIRED FOR LICENSE",
                audioProfile: "cw",
                difficulty: 2,
                historicalContext: "On February 23, 2007, the FCC eliminated the morse code proficiency requirement for all US amateur radio license classes, ending a 90-year tradition. This opened amateur radio to a new generation who could focus on digital modes, satellites, and emergency communications without first mastering CW. Many traditionalists mourned the end of the 'gentleman's agreement' that CW was the universal language of ham radio. Others celebrated the removal of barriers to entry. Ironically, CW operation INCREASED after the requirement was dropped - many operators discovered they enjoyed it when it became optional rather than mandatory.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: The Final Exam",
                            description: "Decode the announcement that ended the exam requirement",
                            speed: 13, // The old requirement speed
                        }
                    ],
                    hints: ["License change", "No longer required", "FCC ruling"],
                    passingAccuracy: 85,
                },
                artifacts: [
                    {
                        id: "artifact_2007_license",
                        name: "Post-2007 License Certificate",
                        description: "First ham license issued without morse code requirement",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era9/new_license.png"
                    }
                ],
                quiz: [
                    {
                        question: "What happened to CW usage after the requirement was dropped?",
                        options: [
                            "It nearly disappeared",
                            "It actually increased",
                            "It stayed exactly the same",
                            "Only old timers continued"
                        ],
                        correctAnswer: 1,
                        explanation: "Paradoxically, CW operation increased after becoming optional. When it was no longer a barrier to licensing, many hams discovered they genuinely enjoyed it.",
                    }
                ],
                rewards: { xp: 1200 },
                triviaLinks: [183], // Code Requirement Changes
            },
            {
                id: "era9_event2",
                eranumber: 9,
                eventNumber: 2,
                title: "Curiosity Rover",
                date: "August 2012",
                location: "Mars",
                location: "Mars",
                message: "JPL JPL JPL",
                audioProfile: "cw",
                difficulty: 3,
                historicalContext: "NASA's Curiosity rover (and later Perseverance) has holes in its aluminum wheels that leave a specific track pattern in the Martian soil. The holes spell out this message in Morse code, literally branding the surface of another planet with the lab's initials.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Mars Tracks",
                            description: "Decode the pattern in the soil",
                            speed: 18,
                        },
                        {
                            id: "visual",
                            name: "Visual: Tire Tracks",
                            description: "Read the visual pattern",
                            visualAnimation: true,
                            visualType: "spark", // Using spark for 'raw' feel
                            speed: 15,
                        }
                    ],
                    hints: ["NASA Laboratory", "Three letters", "Repeated"],
                    passingAccuracy: 100,
                },
                artifacts: [
                    {
                        id: "artifact_mars_wheel",
                        name: "Curiosity Rover Wheel",
                        description: "Extreme close-up of a Mars rover wheel showing the precisely engineered holes that spell out 'JPL' in Morse code as it traverses the Martian soil.",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era9/mars_wheel.png"
                    }
                ],
                rewards: { xp: 1400 },
                triviaLinks: [169], // Curiosity Rover
            },
            {
                id: "era9_event3",
                eraNumber: 9,
                eventNumber: 3,
                title: "The Last Telegram",
                date: "July 14, 2013",
                location: "New Delhi, India",
                location: "New Delhi, India",
                message: "THIS IS THE LAST TELEGRAM SENT BY BSNL INDIA",
                audioProfile: "cw",
                difficulty: 2,
                historicalContext: "On July 14, 2013, India's state-owned telecom BSNL closed its 160-year-old telegraph service. Thousands flocked to offices to send one last souvenir message. It marked the definitive end of the telegraph as a mass communication tool.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Closing Day",
                            description: "Decode the historic final message",
                            speed: 18,
                        },
                        {
                            id: "visual",
                            name: "Visual: Paper Tape",
                            description: "Read the printed strip",
                            visualAnimation: true,
                            visualType: "tape",
                            speed: 15,
                        }
                    ],
                    hints: ["End of service", "Company name (BSNL)", "Country name"],
                    passingAccuracy: 90,
                },
                artifacts: [
                    {
                        id: "artifact_last_telegram_receipt",
                        name: "India's Last Telegram",
                        description: "An official BSNL receipt from the final day of India's 160-year telegraph service, stamped July 14, 2013.",
                        rarity: "epic",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era9/last_telegram.png"
                    }
                ],
                rewards: { xp: 1450 },
                triviaLinks: [170], // The Last Telegram
            },
            {
                id: "era9_event4",
                eraNumber: 9,
                eventNumber: 4,
                title: "Interstellar",
                date: "2014",
                location: "The Tesseract",
                location: "The Tesseract",
                message: "STAY",
                audioProfile: "cw",
                difficulty: 4,
                historicalContext: "In the film 'Interstellar', the protagonist communicates across dimensions using gravitational waves to manipulate the second hand of a watch, tapping out Morse code. It showcased Morse as a fundamental, timeless language.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: The Watch",
                            description: "Decode the message from the future",
                            speed: 22,
                        }
                    ],
                    hints: ["Don't go", "One word", "Father's message"],
                    passingAccuracy: 100,
                },
                triviaLinks: [171], // Interstellar
                artifacts: [
                    {
                        id: "artifact_hamilton_watch",
                        name: "Hamilton Khaki Field Murph",
                        description: "A precision watch with 'Eureka' printed in Morse code on the second hand, from the film Interstellar.",
                        rarity: "legendary",
                        collectionMethod: "perfect_copy",
                        imageFile: "/assets/timeline/era9/hamilton_watch.png"
                    }
                ],
                rewards: { xp: 1500 }
            },
            {
                id: "era9_event5",
                eraNumber: 9,
                eventNumber: 5,
                title: "Summits on the Air",
                date: "Present Day",
                location: "Alpine Peak (SOTA)",
                location: "Alpine Peak (SOTA)",
                message: "CQ SOTA CQ SOTA DE AG6QO K",
                audioProfile: "cw",
                difficulty: 4,
                historicalContext: "Modern Morse (CW) is thriving in outdoor adventure activities like SOTA (Summits on the Air). Operators hike lightweight radios to mountain peaks. CW is preferred because it's lightweight and gets through with low power (QRP) when voice fails.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: Mountain Top",
                            description: "Copy the SOTA activation call",
                            speed: 25,
                            qsb: true // Fading signals!
                        }
                    ],
                    hints: ["Calling SOTA", "Callsign format", "Invitation to transmit (K)"],
                    passingAccuracy: 95,
                },
                artifacts: [
                    {
                        id: "artifact_qrp_radio",
                        name: "Elecraft KX2 Transceiver",
                        description: "A modern ultra-portable QRP radio often used for Summits on the Air (SOTA) activation.",
                        rarity: "rare",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era9/qrp_radio.png"
                    }
                ],
                rewards: { xp: 1600 }
            },
            {
                id: "era9_event6",
                eraNumber: 9,
                eventNumber: 6,
                title: "The Legacy Lives On",
                date: "To the Future",
                location: "Your Station",
                location: "Your Station",
                message: "CW FOREVER",
                audioProfile: "cw",
                difficulty: 1,
                historicalContext: "From the first click in 1844 to the Mars rovers, Morse code has connected humanity for nearly two centuries. By learning this skill, you are keeping a vital tradition alive. The key is now in your hands.",
                decodingChallenge: {
                    modes: [
                        {
                            id: "standard",
                            name: "Standard: The Torch",
                            description: "Accept the legacy",
                            speed: 20,
                        }
                    ],
                    hints: ["The code endures", "Forever", "Two words"],
                    passingAccuracy: 100,
                },
                artifacts: [
                    {
                        id: "artifact_golden_key_final",
                        name: "Supreme Master Golden Key",
                        description: "An ornate, engraved solid gold telegraph key awarded for total Morse code mastery.",
                        rarity: "legendary",
                        collectionMethod: "event_completion",
                        imageFile: "/assets/timeline/era9/golden_key_final.png"
                    }
                ],
                rewards: {
                    xp: 5000,
                    achievements: ["time_traveler", "perfectionist"],
                    themes: ["matrix_digital"]
                }
            }
        ],
    }
];

// ============================================
// ACHIEVEMENT DEFINITIONS
// ============================================

export const TIMELINE_ACHIEVEMENTS = {
    telegraph_pioneer: {
        id: "telegraph_pioneer",
        name: "Telegraph Pioneer",
        description: "Complete Era 1: The First Telegram",
        icon: "🏆",
        xp: 500,
    },
    transatlantic: {
        id: "transatlantic",
        name: "Transatlantic",
        description: "Master visual Morse decoding in Era 2",
        icon: "🌊",
        xp: 500,
    },
    battlefield_operator: {
        id: "battlefield_operator",
        name: "Battlefield Operator",
        description: "Complete Era 3: Civil War communications",
        icon: "⚔️",
        xp: 500,
    },
    coast_to_coast: {
        id: "coast_to_coast",
        name: "Coast to Coast",
        description: "Complete Era 4: Golden Spike",
        icon: "🚂",
        xp: 500,
    },
    savior_of_seas: {
        id: "savior_of_seas",
        name: "Savior of the Seas",
        description: "Complete Titanic distress call event",
        icon: "💔",
        xp: 750,
    },
    secret_agent: {
        id: "secret_agent",
        name: "Secret Agent",
        description: "Complete Era 6: WWII espionage",
        icon: "🕵️",
        xp: 1000,
    },
    space_pioneer: {
        id: "space_pioneer",
        name: "Space Pioneer",
        description: "Complete Era 7: Apollo era",
        icon: "🚀",
        xp: 1000,
    },
    digital_native: {
        id: "digital_native",
        name: "Digital Native",
        description: "Complete Era 8: Digital age",
        icon: "💻",
        xp: 1000,
    },
    time_traveler: {
        id: "time_traveler",
        name: "Time Traveler",
        description: "Complete ALL 9 eras",
        icon: "⏰",
        xp: 2000,
    },
    perfectionist: {
        id: "perfectionist",
        name: "Perfectionist",
        description: "Perfect copy (100%) on all events",
        icon: "🎯",
        xp: 5000,
    },
    artifact_hunter: {
        id: "artifact_hunter",
        name: "Artifact Hunter",
        description: "Collect all 40+ artifacts",
        icon: "💎",
        xp: 3000,
    }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get era by number
 */
export const getEraByNumber = (number) => {
    return TIMELINE_ERAS.find(era => era.number === number);
};

/**
 * Get event by ID
 */
export const getEventById = (eventId) => {
    for (const era of TIMELINE_ERAS) {
        const event = era.events.find(e => e.id === eventId);
        if (event) return event;
    }
    return null;
};

/**
 * Check if era is unlocked based on requirements
 */
export const checkEraUnlocked = (era, userStats, userProgress) => {
    // Era 1 is always unlocked
    if (era.number === 1 || era.unlockType === 'always') {
        return { unlocked: true, method: 'default' };
    }

    // Check if manually unlocked in database
    if (userProgress && userProgress[era.number]?.unlocked) {
        return { unlocked: true, method: userProgress[era.number].unlock_method };
    }

    // Soft locks - show warning but allow
    if (era.unlockType === 'soft') {
        return { unlocked: true, soft: true, recommended: era.unlockRequirements };
    }

    // Hard locks - check requirements
    if (era.unlockType === 'hard' && era.unlockRequirements) {
        const { type, count, requirements } = era.unlockRequirements;

        const metRequirements = requirements.filter(req => {
            if (!req || typeof req.check !== 'function') {
                console.error(`[checkEraUnlocked] Invalid requirement in Era ${era.number}:`, req);
                return false;
            }
            return req.check(userStats);
        });

        if (type === 'all' || !type) {
            return { unlocked: metRequirements.length === requirements.length, metCount: metRequirements.length, totalCount: requirements.length };
        } else if (type === 'any') {
            return { unlocked: metRequirements.length >= count, metCount: metRequirements.length, requiredCount: count };
        }
    }

    // --- SHORTCUTS ---
    // NOTE: Eras 1-3 are always accessible (Era 1='always', Eras 2-3='soft')
    // Shortcuts only apply to hard-locked eras (4+)

    // 1. Skill Level Shortcuts
    if (userStats.skillLevel >= 4 && era.number <= 7) return { unlocked: true, method: 'shortcut_skill' };
    if (userStats.skillLevel >= 3 && era.number <= 5) return { unlocked: true, method: 'shortcut_skill' };

    // 2. Streak Shortcuts
    if (userStats.currentStreak >= 30) return { unlocked: true, method: 'shortcut_streak' };

    return { unlocked: false };
};

/**
 * Calculate overall timeline progress percentage
 */
export const calculateTimelineProgress = (userProgress) => {
    const totalEvents = TIMELINE_ERAS.reduce((sum, era) => sum + (era.events?.length || 0), 0);
    // Total completed events recorded in userProgress (events_completed per era)
    const completedEvents = Object.values(userProgress || {}).reduce((sum, prog) => sum + (prog.events_completed || 0), 0);
    if (totalEvents === 0) return 0;
    return Math.round((completedEvents / totalEvents) * 100);
};




